import { useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowDownRight,
  ArrowUpLeft,
  ArrowUpRight,
  Check,
  ChevronDown,
  ImagePlus,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { CoreDrawer } from "@/components/common/CoreDrawer";
import { GenieAvatar } from "@/components/GenieAvatar";
import { Button } from "@/components/ui/button";
import { EnhancedImageUpload } from "@/components/ui/enhanced-image-upload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { useRole } from "@/hooks/auth/useRole";
import { useAccountBranding } from "@/hooks/branding/useAccountBranding";
import { cn } from "@/lib/utils";
import { widgetBackgroundToCss } from "@/lib/widgetBackgroundPresets";
import { ProposedWidgetStyle, type, WidgetStyleVisionService } from "@/services/branding/WidgetStyleVisionService";
import {
  AVATAR_ALIGN_OPTIONS,
  BackgroundPatternId,
  ChatEmptyStateElement,
  DEFAULT_BRANDING,
  ExtractedColorPalette,
  FULL_PAGE_LAYOUT_PRESETS,
  FullPageAvatarAlign,
  FullPageRailPosition,
  FullPageStyle,
  FullPageTranscriptPosition,
  FullPageWelcomePosition,
  GenieBranding,
  RAIL_POSITION_OPTIONS,
  TRANSCRIPT_POSITION_OPTIONS,
  WELCOME_POSITION_OPTIONS,
  WidgetActionButtonStyle,
  WidgetBackgroundStyle,
  WidgetButtonStyle,
  WidgetChatButtonStyle,
  WidgetCaptionStyle,
  WidgetChatStyle,
  WidgetHeaderStyle,
  WidgetPosition,
} from "@/types/ui/branding";
import { fileToCompressedBase64 } from "@/utils/image/toBase64";
import { ChatWidgetEmbed } from "./ChatWidgetEmbed";
import { DistributionWidgetEmbed } from "./DistributionWidgetEmbed";
import { EmailWidgetEmbed } from "./EmailWidgetEmbed";
import { LeadConversionsSettings } from "./LeadConversionsSettings";
import { FullPageEmbed } from "./FullPageEmbed";
import FullPagePreview from "./FullPagePreview";
import { useEmbedCode } from "./hooks/useEmbedCode";
import { IFrameEmbed } from "./IFrameEmbed";
import { ReactEmbed } from "./ReactEmbed";
import { ScriptEmbed } from "./ScriptEmbed";
import WidgetPreview from "./WidgetPreview";
import WidgetStyleCatalog from "./WidgetStyleCatalog";
import type { WebEmbedTab } from "./webEmbedTabs";

import type { Agent } from "@/types/agent/agent";
import type { WidgetTemplateId, WidgetSize } from "@/types/ui/widget-styles";

interface WebEmbedModalProps {
  genie: Agent;
  isOpen: boolean;
  onClose: () => void;
  onSaveTemplate?: (templateId: WidgetTemplateId) => void;
  initialTab?: WebEmbedTab;
}

const POPUP_SIZES: { id: WidgetSize; label: string; description: string; dimensions: string }[] = [
  { id: "small", label: "Small", description: "Compact, minimal footprint", dimensions: "320 × 480" },
  { id: "medium", label: "Medium", description: "Balanced — recommended", dimensions: "380 × 580" },
  { id: "large", label: "Large", description: "More breathing room", dimensions: "440 × 600" },
  { id: "xl", label: "XL", description: "Full-width panel", dimensions: "900 × 700" },
];

const POPUP_POSITIONS: { id: WidgetPosition; label: string; icon: typeof ArrowDownRight }[] = [
  { id: "bottom-right", label: "Bottom right", icon: ArrowDownRight },
  { id: "bottom-left", label: "Bottom left", icon: ArrowDownLeft },
  { id: "top-right", label: "Top right", icon: ArrowUpRight },
  { id: "top-left", label: "Top left", icon: ArrowUpLeft },
];

const BUTTON_LABEL_MAX_LENGTH = 40;

const WIDGET_OPTIONS = [
  { value: "standard", label: "Standard Widget", description: "Popup and full-page experiences" },
  { value: "chat", label: "Chat Widget", description: "Text conversations only" },
  { value: "email", label: "Email Widget", description: "Questions answered by email" },
  { value: "phone", label: "Phone Widget", description: "One tap to call your Genie" },
  { value: "qr", label: "QR Code Widget", description: "Scan to open your Genie" },
] as const;

const getBrandColorSwatches = (branding?: GenieBranding | null): string[] => {
  if (!branding) return [];
  const colors = [branding.primaryColor, branding.secondaryColor, ...(branding.extractedPalette?.palette || [])].filter(
    (color): color is string => !!color,
  );

  return Array.from(new Set(colors.map((c) => c.toUpperCase())));
};

const CONFIG_SECTIONS = [
  "brand",
  "style",
  "size",
  "position",
  "content",
  "caption",
  "avatar",
  "header",
  "colors",
  "chat",
] as const;
const FULL_PAGE_CONFIG_SECTIONS = ["layout", "content", "avatar", "background", "buttons", "text", "chat"] as const;
type FullPageConfigSectionId = (typeof FULL_PAGE_CONFIG_SECTIONS)[number];
const EMPTY_STATE_ELEMENTS: ChatEmptyStateElement[] = ["icon", "title", "subtitle"];
const EMPTY_STATE_LABELS: Record<ChatEmptyStateElement, string> = {
  icon: "Icon",
  title: "Title",
  subtitle: "Subtitle",
};
const DEFAULT_PATTERN_SIZE: Record<BackgroundPatternId, number> = {
  dots: 14,
  grid: 20,
  "diagonal-lines": 12,
};
type ConfigSectionId = (typeof CONFIG_SECTIONS)[number];

interface CollapsibleSectionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const CollapsibleSection = ({ title, isOpen, onToggle, children }: CollapsibleSectionProps) => (
  <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
    <div className="rounded-[10px] bg-white overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-5 py-4 flex items-center justify-between bg-gray-50/50 hover:bg-gray-100/50 transition-colors"
      >
        <span className="font-medium text-gray-900">{title}</span>
        <ChevronDown className={cn("h-4 w-4 text-gray-500 transition-transform", isOpen && "rotate-180")} />
      </button>
      {isOpen && <div className="px-5 py-4 border-t">{children}</div>}
    </div>
  </div>
);

/** Compact "Brand colors" popover trigger, used alongside the primary
 *  ColorSwatchButton picker in every color field. The color picker is the
 *  main control; this is a secondary shortcut into the genie/team's existing
 *  brand palette, so it doesn't compete for space or attention. */
const BrandColorQuickPicks = ({ colors, onPick }: { colors: string[]; onPick: (color: string) => void }) => {
  if (colors.length === 0) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Pick from brand colors"
          className="flex h-11 items-center gap-1.5 rounded-lg border border-gray-200 px-3 text-xs font-medium text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50 flex-shrink-0"
        >
          <span className="flex -space-x-1">
            {colors.slice(0, 3).map((color) => (
              <span
                key={color}
                className="h-4 w-4 rounded-full border border-white"
                style={{ backgroundColor: color }}
              />
            ))}
          </span>
          Brand colors
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <p className="mb-2 text-xs font-medium text-gray-500">Brand colors</p>
        <div className="flex flex-wrap gap-2" style={{ maxWidth: 240 }}>
          {colors.map((color) => (
            <PopoverClose asChild key={color}>
              <button
                type="button"
                title={color}
                onClick={() => onPick(color)}
                className="h-11 w-11 rounded-full border border-gray-300 transition-transform hover:scale-110 flex-shrink-0"
                style={{ backgroundColor: color }}
              />
            </PopoverClose>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

/** Clean, on-brand replacement for the raw native `<input type="color">`
 *  swatch (which renders as an unstyled OS control). Shows the current color
 *  as a rounded preview chip; clicking it still opens the native color
 *  picker via a visually-hidden input, so no picker functionality is lost.
 *  `unset` paints the chip as "no colour chosen" (white with a diagonal line)
 *  for fields whose default differs per surface, so the chip doesn't claim a
 *  colour that isn't applied; `value` still seeds the picker. */
const ColorSwatchButton = ({
  value,
  onChange,
  label,
  className,
  unset = false,
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  className?: string;
  unset?: boolean;
}) => (
  <label
    title={unset ? `${label ?? "Color"}: default` : label}
    className={cn(
      "relative flex h-11 w-11 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg border border-gray-200 shadow-sm transition-all hover:scale-105 hover:border-gray-300",
      className,
    )}
    style={
      unset
        ? {
            backgroundColor: "#FFFFFF",
            backgroundImage:
              "linear-gradient(to top right, transparent calc(50% - 1px), #CBD5E1 calc(50% - 1px), #CBD5E1 calc(50% + 1px), transparent calc(50% + 1px))",
          }
        : { backgroundColor: value }
    }
  >
    <input
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      aria-label={label}
    />
  </label>
);

const WebEmbedModal = ({ genie, isOpen, onClose, initialTab = "widget" }: WebEmbedModalProps) => {
  const [selectedWidget, setSelectedWidget] = useState("standard");
  const [standardTab, setStandardTab] = useState<WebEmbedTab>(initialTab);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedWidget("standard");
    setStandardTab(initialTab);
  }, [isOpen, initialTab]);

  const savedTemplate = genie.branding?.embedStyle?.widgetTemplate || "classic";
  const [selectedTemplate, setSelectedTemplate] = useState<WidgetTemplateId>(savedTemplate);
  const [selectedSize, setSelectedSize] = useState<WidgetSize>("medium");
  const [selectedPosition, setSelectedPosition] = useState<WidgetPosition>(
    genie.branding?.embedStyle?.widgetPosition || "bottom-right",
  );
  const [buttonLabel, setButtonLabel] = useState<string>(genie.branding?.embedStyle?.widgetButtonLabel || "");
  const [backgroundColor, setBackgroundColor] = useState<string>(
    genie.branding?.embedStyle?.widgetBackgroundColor || "",
  );
  const [hideWelcomeText, setHideWelcomeText] = useState<boolean>(
    genie.branding?.embedStyle?.widgetHideWelcomeText ?? false,
  );
  const [hideWelcomeMessage, setHideWelcomeMessage] = useState<boolean>(
    genie.branding?.embedStyle?.hideWelcomeMessage ?? false,
  );
  const [hideAttribution, setHideAttribution] = useState<boolean>(genie.branding?.hideAttribution ?? false);
  const [background, setBackground] = useState<WidgetBackgroundStyle | null>(
    genie.branding?.embedStyle?.background ?? null,
  );
  const [headerLogoSize, setHeaderLogoSize] = useState<number>(genie.branding?.embedStyle?.headerLogoSize ?? 36);
  const [headerStyle, setHeaderStyle] = useState<WidgetHeaderStyle | null>(
    genie.branding?.embedStyle?.headerStyle ?? null,
  );
  const [ctaShape, setCtaShape] = useState<"rounded" | "square" | "pill">(
    genie.branding?.embedStyle?.buttonStyle ?? "pill",
  );
  const [ctaButton, setCtaButton] = useState<WidgetButtonStyle | null>(genie.branding?.embedStyle?.ctaButton ?? null);
  const [chatButton, setChatButton] = useState<WidgetChatButtonStyle | null>(
    genie.branding?.embedStyle?.chatButton ?? null,
  );
  const [actionButtons, setActionButtons] = useState<WidgetActionButtonStyle | null>(
    genie.branding?.embedStyle?.actionButtons ?? null,
  );
  const [avatarImageUrl, setAvatarImageUrl] = useState<string | undefined>(
    genie.branding?.embedStyle?.avatarImageUrl || undefined,
  );
  const [avatarShowLogoOverlay, setAvatarShowLogoOverlay] = useState<boolean>(
    genie.branding?.embedStyle?.avatarShowLogoOverlay ?? false,
  );
  const [avatarSizeScale, setAvatarSizeScale] = useState<number>(genie.branding?.embedStyle?.avatarSizeScale ?? 1);
  const [chatStyle, setChatStyle] = useState<WidgetChatStyle | null>(genie.branding?.embedStyle?.chatStyle ?? null);
  const [caption, setCaption] = useState<WidgetCaptionStyle | null>(genie.branding?.embedStyle?.caption ?? null);
  const [openSections, setOpenSections] = useState<Set<ConfigSectionId>>(new Set());

  const [brandLogoUrl, setBrandLogoUrl] = useState<string | undefined>(
    genie.branding?.logoUrl || genie.branding?.customAvatarUrl || undefined,
  );
  const [brandPrimaryColor, setBrandPrimaryColor] = useState<string>(
    genie.branding?.primaryColor || DEFAULT_BRANDING.primaryColor,
  );
  const [brandSecondaryColor, setBrandSecondaryColor] = useState<string | null>(genie.branding?.secondaryColor ?? null);
  const [brandAccentColor, setBrandAccentColor] = useState<string | null>(genie.branding?.accentColor ?? null);
  const [brandGradientEnabled, setBrandGradientEnabled] = useState<boolean>(genie.branding?.gradientEnabled ?? false);
  const [brandExtractedPalette, setBrandExtractedPalette] = useState<ExtractedColorPalette | null>(
    genie.branding?.extractedPalette ?? null,
  );

  const [fullPageBackground, setFullPageBackground] = useState<WidgetBackgroundStyle | null>(
    genie.branding?.embedStyle?.fullPageStyle?.background ?? genie.branding?.embedStyle?.fullPageBackground ?? null,
  );
  const savedFullPageStyle = genie.branding?.embedStyle?.fullPageStyle ?? null;
  const [fpLinksResourcesPosition, setFpLinksResourcesPosition] = useState<FullPageRailPosition>(
    savedFullPageStyle?.layout?.linksResourcesPosition ?? "right",
  );
  const [fpAvatarAlign, setFpAvatarAlign] = useState<FullPageAvatarAlign>(
    savedFullPageStyle?.layout?.avatarAlign ?? "center",
  );
  const [fpWelcomePosition, setFpWelcomePosition] = useState<FullPageWelcomePosition>(
    savedFullPageStyle?.layout?.welcomePosition ?? (savedFullPageStyle?.hideWelcomeMessage ? "hidden" : "above-avatar"),
  );
  const [fpTranscriptPosition, setFpTranscriptPosition] = useState<FullPageTranscriptPosition>(
    savedFullPageStyle?.layout?.transcriptPosition ?? "right",
  );
  const [fpCtaButton, setFpCtaButton] = useState<WidgetButtonStyle | null>(savedFullPageStyle?.ctaButton ?? null);
  const [fpChatButton, setFpChatButton] = useState<WidgetChatButtonStyle | null>(
    savedFullPageStyle?.chatButton ?? null,
  );
  const [fpActionButtons, setFpActionButtons] = useState<WidgetActionButtonStyle | null>(
    savedFullPageStyle?.actionButtons ?? null,
  );
  const [fpWelcomeTextColor, setFpWelcomeTextColor] = useState<string | null>(
    savedFullPageStyle?.welcomeTextColor ?? null,
  );
  const [fpAvatarImageUrl, setFpAvatarImageUrl] = useState<string | undefined>(
    savedFullPageStyle?.avatarImageUrl || undefined,
  );
  const [fpAvatarShowLogoOverlay, setFpAvatarShowLogoOverlay] = useState<boolean>(
    savedFullPageStyle?.avatarShowLogoOverlay ?? false,
  );
  const [fpAvatarSizeScale, setFpAvatarSizeScale] = useState<number>(savedFullPageStyle?.avatarSizeScale ?? 1);
  const [fpChatStyle, setFpChatStyle] = useState<WidgetChatStyle | null>(savedFullPageStyle?.chatStyle ?? null);
  const [fpOpenSections, setFpOpenSections] = useState<Set<FullPageConfigSectionId>>(
    new Set(FULL_PAGE_CONFIG_SECTIONS),
  );
  const toggleFpSection = (id: FullPageConfigSectionId) => {
    setFpOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const fpAllSectionsOpen = fpOpenSections.size === FULL_PAGE_CONFIG_SECTIONS.length;
  const toggleAllFpSections = () => {
    setFpOpenSections(fpAllSectionsOpen ? new Set() : new Set(FULL_PAGE_CONFIG_SECTIONS));
  };

  const [isAnalyzingStyleImage, setIsAnalyzingStyleImage] = useState(false);
  const [styleAppliedFromImage, setStyleAppliedFromImage] = useState(false);

  const updateAgent = useUpdateAgent();
  const { isInternalAdmin } = useRole();
  const canEditAttribution = isInternalAdmin();
  const { branding: teamBranding } = useAccountBranding();

  const genieSwatches = getBrandColorSwatches(genie.branding);
  const backgroundSwatches = genieSwatches.length > 0 ? genieSwatches : getBrandColorSwatches(teamBranding);

  const allSectionsOpen = openSections.size === CONFIG_SECTIONS.length;
  const toggleSection = (id: ConfigSectionId) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAllSections = () => {
    setOpenSections(allSectionsOpen ? new Set() : new Set(CONFIG_SECTIONS));
  };

  const primaryColor = genie.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const { scriptEmbedCode, iframeEmbedCode, iframeReactCode, fullPageHtmlCode, fullPageReactCode, baseUrl, urlName } =
    useEmbedCode(genie, selectedTemplate, selectedSize, selectedPosition, buttonLabel || null, caption);

  const hasCustomEmbedStyling = !!(
    genie.branding?.embedStyle?.background ||
    genie.branding?.embedStyle?.headerStyle ||
    genie.branding?.embedStyle?.headerLogoSize ||
    genie.branding?.embedStyle?.ctaButton ||
    genie.branding?.embedStyle?.chatButton ||
    genie.branding?.embedStyle?.actionButtons ||
    genie.branding?.embedStyle?.widgetBackgroundColor ||
    genie.branding?.embedStyle?.widgetHideWelcomeText ||
    genie.branding?.embedStyle?.hideWelcomeMessage ||
    genie.branding?.embedStyle?.avatarImageUrl ||
    (genie.branding?.embedStyle?.avatarSizeScale && genie.branding.embedStyle.avatarSizeScale !== 1) ||
    genie.branding?.embedStyle?.chatStyle ||
    (genie.branding?.embedStyle?.buttonStyle && genie.branding.embedStyle.buttonStyle !== "pill")
  );

  const hasUnsavedChanges =
    selectedPosition !== (genie.branding?.embedStyle?.widgetPosition || "bottom-right") ||
    buttonLabel !== (genie.branding?.embedStyle?.widgetButtonLabel || "") ||
    backgroundColor !== (genie.branding?.embedStyle?.widgetBackgroundColor || "") ||
    hideWelcomeText !== (genie.branding?.embedStyle?.widgetHideWelcomeText ?? false) ||
    hideWelcomeMessage !== (genie.branding?.embedStyle?.hideWelcomeMessage ?? false) ||
    JSON.stringify(background) !== JSON.stringify(genie.branding?.embedStyle?.background ?? null) ||
    headerLogoSize !== (genie.branding?.embedStyle?.headerLogoSize ?? 36) ||
    JSON.stringify(headerStyle) !== JSON.stringify(genie.branding?.embedStyle?.headerStyle ?? null) ||
    ctaShape !== (genie.branding?.embedStyle?.buttonStyle ?? "pill") ||
    JSON.stringify(ctaButton) !== JSON.stringify(genie.branding?.embedStyle?.ctaButton ?? null) ||
    JSON.stringify(chatButton) !== JSON.stringify(genie.branding?.embedStyle?.chatButton ?? null) ||
    JSON.stringify(actionButtons) !== JSON.stringify(genie.branding?.embedStyle?.actionButtons ?? null) ||
    avatarImageUrl !== (genie.branding?.embedStyle?.avatarImageUrl || undefined) ||
    avatarShowLogoOverlay !== (genie.branding?.embedStyle?.avatarShowLogoOverlay ?? false) ||
    avatarSizeScale !== (genie.branding?.embedStyle?.avatarSizeScale ?? 1) ||
    JSON.stringify(chatStyle) !== JSON.stringify(genie.branding?.embedStyle?.chatStyle ?? null) ||
    JSON.stringify(caption) !== JSON.stringify(genie.branding?.embedStyle?.caption ?? null) ||
    (canEditAttribution && hideAttribution !== (genie.branding?.hideAttribution ?? false));

  const liveEmbedStyle = {
    ...DEFAULT_BRANDING.embedStyle,
    ...genie.branding?.embedStyle,
    widgetBackgroundColor: backgroundColor || null,
    widgetHideWelcomeText: hideWelcomeText,
    hideWelcomeMessage,
    background,
    headerLogoSize,
    headerStyle,
    buttonStyle: ctaShape,
    ctaButton,
    chatButton,
    actionButtons,
    avatarImageUrl: avatarImageUrl || null,
    avatarShowLogoOverlay,
    avatarSizeScale,
    chatStyle,
    caption,
  };

  const buildEmbedStylePayload = (overrides: Partial<typeof liveEmbedStyle> = {}) => ({
    ...DEFAULT_BRANDING.embedStyle,
    ...genie.branding?.embedStyle,
    widgetPosition: selectedPosition,
    widgetButtonLabel: buttonLabel || null,
    widgetBackgroundColor: backgroundColor || null,
    widgetHideWelcomeText: hideWelcomeText,
    hideWelcomeMessage,
    background,
    headerLogoSize,
    headerStyle,
    buttonStyle: ctaShape,
    ctaButton,
    chatButton,
    actionButtons,
    avatarImageUrl: avatarImageUrl || null,
    avatarShowLogoOverlay,
    avatarSizeScale,
    chatStyle,
    caption,
    ...overrides,
  });

  const hasUnsavedBrandChanges =
    brandLogoUrl !== (genie.branding?.logoUrl || genie.branding?.customAvatarUrl || undefined) ||
    brandPrimaryColor !== (genie.branding?.primaryColor || DEFAULT_BRANDING.primaryColor) ||
    brandSecondaryColor !== (genie.branding?.secondaryColor ?? null) ||
    brandAccentColor !== (genie.branding?.accentColor ?? null) ||
    brandGradientEnabled !== (genie.branding?.gradientEnabled ?? false);

  const handleBrandLogoUploadComplete = (url: string, colors: ExtractedColorPalette | null) => {
    setBrandLogoUrl(url);
    if (colors) {
      setBrandExtractedPalette(colors);
      setBrandPrimaryColor(colors.suggestedPrimary);
      setBrandSecondaryColor(colors.suggestedSecondary);
      setBrandGradientEnabled(true);
    }
  };

  const handleSaveBrandSettings = async () => {
    try {
      await updateAgent.mutateAsync({
        agent: genie,
        updateData: {
          branding: {
            ...DEFAULT_BRANDING,
            ...genie.branding,
            logoUrl: brandLogoUrl || null,
            customAvatarUrl: brandLogoUrl || null,
            primaryColor: brandPrimaryColor,
            secondaryColor: brandSecondaryColor,
            accentColor: brandAccentColor,
            gradientEnabled: brandGradientEnabled,
            extractedPalette: brandExtractedPalette,
          },
        },
      });
      toast.success("Brand settings updated everywhere — not just this widget.");
    } catch (error) {
      console.error("Failed to update brand settings:", error);
      toast.error("Failed to save brand settings");
    }
  };

  /** Fans a vision-proposed style diff out into this modal's own editable
   *  state — the same setters manual editing uses — so the existing live
   *  WidgetPreview reflects it immediately and every field stays editable
   *  through its normal control. Nothing is persisted here; the user still
   *  has to hit Save. */
  const applyProposedEmbedStyle = (proposed: ProposedWidgetStyle) => {
    const sectionsToOpen = new Set<ConfigSectionId>();
    const touchedTopLevelBrand = Object.keys(proposed.topLevel).length > 0;

    if (proposed.topLevel.primaryColor) {
      setBrandPrimaryColor(proposed.topLevel.primaryColor);
      sectionsToOpen.add("brand");
    }
    if (proposed.topLevel.secondaryColor) {
      setBrandSecondaryColor(proposed.topLevel.secondaryColor);
      setBrandGradientEnabled(true);
      sectionsToOpen.add("brand");
    }
    if (proposed.topLevel.accentColor) {
      setBrandAccentColor(proposed.topLevel.accentColor);
      sectionsToOpen.add("brand");
    }
    if (proposed.topLevel.gradientEnabled !== undefined) {
      setBrandGradientEnabled(proposed.topLevel.gradientEnabled);
      sectionsToOpen.add("brand");
    }

    const s = proposed.embedStyle;
    if (s.widgetTemplate) {
      setSelectedTemplate(s.widgetTemplate);
      sectionsToOpen.add("style");
    }
    if (s.buttonStyle) {
      setCtaShape(s.buttonStyle);
      sectionsToOpen.add("colors");
    }
    if (s.widgetBackgroundColor) {
      setBackgroundColor(s.widgetBackgroundColor);
      sectionsToOpen.add("content");
    }
    if (s.background) {
      setBackground(s.background);
      sectionsToOpen.add("content");
    }
    if (s.fullPageBackground) {
      setFullPageBackground(s.fullPageBackground);
      sectionsToOpen.add("content");
    }
    if (s.headerStyle) {
      setHeaderStyle(s.headerStyle);
      sectionsToOpen.add("header");
    }
    if (s.headerLogoSize !== undefined) {
      setHeaderLogoSize(s.headerLogoSize);
      sectionsToOpen.add("header");
    }
    if (s.ctaButton) {
      setCtaButton(s.ctaButton);
      sectionsToOpen.add("colors");
    }
    if (s.actionButtons) {
      setActionButtons(s.actionButtons);
      sectionsToOpen.add("colors");
    }
    if (s.avatarSizeScale !== undefined) {
      setAvatarSizeScale(s.avatarSizeScale);
      sectionsToOpen.add("avatar");
    }
    if (s.avatarShowLogoOverlay !== undefined) {
      setAvatarShowLogoOverlay(s.avatarShowLogoOverlay);
      sectionsToOpen.add("avatar");
    }
    if (s.chatStyle) {
      setChatStyle((prev) => ({ ...prev, ...s.chatStyle }));
      sectionsToOpen.add("content");
    }
    if (s.hideWelcomeText !== undefined) {
      setHideWelcomeText(s.hideWelcomeText);
      sectionsToOpen.add("content");
    }
    if (s.hideWelcomeMessage !== undefined) {
      setHideWelcomeMessage(s.hideWelcomeMessage);
      sectionsToOpen.add("content");
    }

    setOpenSections((prev) => new Set([...prev, ...sectionsToOpen]));
    setStyleAppliedFromImage(true);
    toast.success(
      touchedTopLevelBrand
        ? "Style applied from your image — this also updated the genie's overall brand colors. Review below, then Save."
        : "Style applied from your image — review below, then Save.",
    );
  };

  const handleStyleReferenceImage = async (file: File) => {
    setIsAnalyzingStyleImage(true);
    try {
      const base64 = await fileToCompressedBase64(file);
      if (!base64) {
        toast.error("Couldn't read that image — try a different file.");
        return;
      }
      const proposal = await WidgetStyleVisionService.proposeFromImage(base64);
      if (!proposal) {
        toast.error("Couldn't find any recognizable style in that image — try a clearer screenshot.");
        return;
      }
      applyProposedEmbedStyle(proposal);
    } catch (error) {
      console.error("Failed to analyze style reference image:", error);
      toast.error("Failed to analyze image");
    } finally {
      setIsAnalyzingStyleImage(false);
    }
  };

  const buildFullPageStylePayload = (): FullPageStyle => ({
    background: fullPageBackground,
    hideWelcomeMessage: fpWelcomePosition === "hidden",
    layout: {
      linksResourcesPosition: fpLinksResourcesPosition,
      avatarAlign: fpAvatarAlign,
      welcomePosition: fpWelcomePosition,
      transcriptPosition: fpTranscriptPosition,
    },
    avatarImageUrl: fpAvatarImageUrl || null,
    avatarShowLogoOverlay: fpAvatarShowLogoOverlay,
    avatarSizeScale: fpAvatarSizeScale,
    ctaButton: fpCtaButton,
    chatButton: fpChatButton,
    actionButtons: fpActionButtons,
    welcomeTextColor: fpWelcomeTextColor,
    chatStyle: fpChatStyle,
  });

  const DEFAULT_FULL_PAGE_STYLE: FullPageStyle = {
    background: null,
    hideWelcomeMessage: false,
    layout: {
      linksResourcesPosition: "right",
      avatarAlign: "center",
      welcomePosition: "above-avatar",
      transcriptPosition: "right",
    },
    avatarImageUrl: null,
    avatarShowLogoOverlay: false,
    avatarSizeScale: 1,
    ctaButton: null,
    chatButton: null,
    actionButtons: null,
    welcomeTextColor: null,
    chatStyle: null,
  };

  const hasUnsavedFullPageChanges =
    JSON.stringify(buildFullPageStylePayload()) !== JSON.stringify(savedFullPageStyle ?? DEFAULT_FULL_PAGE_STYLE);

  const handleSaveFullPageSettings = async () => {
    try {
      await updateAgent.mutateAsync({
        agent: genie,
        updateData: {
          branding: {
            ...DEFAULT_BRANDING,
            ...genie.branding,
            embedStyle: {
              ...DEFAULT_BRANDING.embedStyle,
              ...genie.branding?.embedStyle,
              fullPageStyle: buildFullPageStylePayload(),
            },
          },
        },
      });
      toast.success("Full page appearance settings saved!");
    } catch (error) {
      console.error("Failed to update full page appearance settings:", error);
      toast.error("Failed to save full page appearance settings");
    }
  };

  /** Quick-start: seeds the CTA/action-button colors from this genie's
   *  overall brand palette (primaryColor/secondaryColor), same colors used
   *  for the avatar, QR codes, and email signature. Doesn't touch layout,
   *  visibility, or background — those stay whatever's already set. Nothing
   *  is saved until the user hits Save. */
  const applyBrandColorsToFullPage = () => {
    setFpCtaButton({ ...fpCtaButton, color: brandPrimaryColor, color2: brandSecondaryColor || undefined });
    setFpActionButtons({
      ...fpActionButtons,
      idleBackgroundColor: `${brandPrimaryColor}14`,
      activeBackgroundColor: brandPrimaryColor,
      iconColor: brandPrimaryColor,
    });
    setFpWelcomeTextColor(null);
    toast.success("Brand colors applied — review below, then Save.");
  };

  /** Quick-start: copies the popup widget's own appearance settings
   *  (background, CTA/action-button colors, avatar photo, hide-welcome
   *  flag) over to the full-page config, so a genie already styled for the
   *  popup doesn't have to be styled twice from scratch. Layout/position
   *  fields are left alone — the popup has no equivalent concept. */
  const applyPopupConfigToFullPage = () => {
    setFullPageBackground(background);
    setFpCtaButton(ctaButton);
    setFpChatButton(chatButton);
    setFpActionButtons(actionButtons);
    setFpAvatarImageUrl(avatarImageUrl);
    setFpAvatarShowLogoOverlay(avatarShowLogoOverlay);
    setFpAvatarSizeScale(avatarSizeScale);
    setFpWelcomePosition(hideWelcomeMessage ? "hidden" : "above-avatar");
    setFpChatStyle(chatStyle);
    toast.success("Popup widget appearance applied — review below, then Save.");
  };

  /** Resets every Full Page Appearance field back to its default value.
   *  Local-only, like the two apply-* actions above — nothing is persisted
   *  until Save is clicked. */
  const resetFullPageToDefaults = () => {
    setFullPageBackground(null);
    setFpLinksResourcesPosition("right");
    setFpAvatarAlign("center");
    setFpWelcomePosition("above-avatar");
    setFpTranscriptPosition("right");
    setFpAvatarImageUrl(undefined);
    setFpAvatarShowLogoOverlay(false);
    setFpAvatarSizeScale(1);
    setFpCtaButton(null);
    setFpChatButton(null);
    setFpActionButtons(null);
    setFpWelcomeTextColor(null);
    setFpChatStyle(null);
    toast.success("Full page appearance reset to defaults — review below, then Save.");
  };

  /** Quick-start: seeds the CTA/action-button colors from this genie's
   *  overall brand palette, mirroring applyBrandColorsToFullPage. Doesn't
   *  touch layout, template, or background — those stay whatever's already
   *  set. Nothing is saved until the user hits Save. */
  const applyBrandColorsToPopup = () => {
    setCtaButton({ ...ctaButton, color: brandPrimaryColor, color2: brandSecondaryColor || undefined });
    setActionButtons({
      ...actionButtons,
      idleBackgroundColor: `${brandPrimaryColor}14`,
      activeBackgroundColor: brandPrimaryColor,
      iconColor: brandPrimaryColor,
    });
    setHeaderStyle({ ...headerStyle, color: brandPrimaryColor, color2: brandSecondaryColor || undefined });
    toast.success("Brand colors applied — review below, then Save.");
  };

  const handleSavePopupSettings = async () => {
    try {
      await updateAgent.mutateAsync({
        agent: genie,
        updateData: {
          branding: {
            ...DEFAULT_BRANDING,
            ...genie.branding,
            hideAttribution: canEditAttribution ? hideAttribution : (genie.branding?.hideAttribution ?? false),
            embedStyle: buildEmbedStylePayload(),
          },
        },
      });
      toast.success("Popup widget settings saved!");
    } catch (error) {
      console.error("Failed to update popup widget settings:", error);
      toast.error("Failed to save popup widget settings");
    }
  };

  const handleResetToDefaults = async () => {
    try {
      const clearedEmbedStyle = buildEmbedStylePayload({
        background: null,
        headerStyle: null,
        headerLogoSize: DEFAULT_BRANDING.embedStyle?.headerLogoSize ?? 36,
        buttonStyle: "pill",
        ctaButton: null,
        chatButton: null,
        actionButtons: null,
        widgetBackgroundColor: null,
        widgetHideWelcomeText: false,
        hideWelcomeMessage: false,
        avatarImageUrl: null,
        avatarShowLogoOverlay: false,
        avatarSizeScale: 1,
        chatStyle: null,
      });

      await updateAgent.mutateAsync({
        agent: genie,
        updateData: {
          branding: {
            ...DEFAULT_BRANDING,
            ...genie.branding,
            embedStyle: clearedEmbedStyle,
          },
        },
      });

      setBackground(null);
      setHeaderStyle(null);
      setHeaderLogoSize(DEFAULT_BRANDING.embedStyle?.headerLogoSize ?? 36);
      setCtaShape("pill");
      setCtaButton(null);
      setChatButton(null);
      setActionButtons(null);
      setBackgroundColor("");
      setHideWelcomeText(false);
      setHideWelcomeMessage(false);
      setAvatarImageUrl(undefined);
      setAvatarShowLogoOverlay(false);
      setAvatarSizeScale(1);
      setChatStyle(null);

      toast.success("Widget appearance reset to defaults");
    } catch (error) {
      console.error("Failed to reset widget appearance:", error);
      toast.error("Failed to reset widget appearance");
    }
  };

  return (
    <CoreDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={`Website Widgets - ${genie.name}`}
      description="Choose a widget, make it yours, and copy the code to add it to your website."
      className="sm:!max-w-[80vw]"
    >
      <Tabs
        value={selectedWidget}
        onValueChange={setSelectedWidget}
        orientation="vertical"
        className="flex min-h-full flex-col gap-6 py-4 lg:h-full lg:min-h-0 lg:flex-row"
      >
        <TabsList
          aria-label="Widget type"
          className="flex h-auto shrink-0 flex-col items-stretch justify-start gap-2 rounded-xl border border-genie-border bg-white p-2 lg:w-52 lg:self-start"
        >
          {WIDGET_OPTIONS.map((widget) => (
            <TabsTrigger
              key={widget.value}
              value={widget.value}
              className="min-h-11 flex-col items-start whitespace-normal rounded-lg border border-transparent px-4 py-3 text-left data-[state=active]:border-genie-primary data-[state=active]:bg-genie-primary/10 data-[state=active]:text-gray-900 data-[state=active]:shadow-none"
            >
              <span className="flex w-full items-center justify-between gap-2 font-semibold">
                {widget.label}{" "}
                {selectedWidget === widget.value && (
                  <Check aria-hidden="true" className="h-4 w-4 shrink-0 text-genie-primary" />
                )}
              </span>
              <span className="mt-1 text-xs font-normal text-gray-500">{widget.description}</span>
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="standard" forceMount className="mt-0 min-h-0 min-w-0 flex-1 data-[state=inactive]:hidden">
          <Tabs
            value={standardTab}
            onValueChange={(value) => setStandardTab(value as WebEmbedTab)}
            className="flex flex-col h-full min-w-0"
          >
            <div className="px-4">
              <TabsList
                aria-label="Standard widget settings"
                className="grid h-auto w-full grid-cols-2 xl:grid-cols-4 mb-0"
              >
                <TabsTrigger className="min-h-11 whitespace-normal" value="widget">
                  Popup Widget
                </TabsTrigger>
                <TabsTrigger className="min-h-11 whitespace-normal" value="appearance">
                  Popup Appearance
                </TabsTrigger>
                <TabsTrigger className="min-h-11 whitespace-normal" value="fullpage">
                  Full Page Embed
                </TabsTrigger>
                <TabsTrigger className="min-h-11 whitespace-normal" value="fullpage-appearance">
                  Full Page Appearance
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Popup Widget tab */}
            <TabsContent value="widget" className="flex-1 mt-0">
              <div className="p-4 space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <WidgetPreview
                    genie={genie}
                    selectedTemplate={selectedTemplate}
                    primaryColor={primaryColor}
                    embedStyle={liveEmbedStyle}
                    avatarImageUrl={avatarImageUrl}
                  />

                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-4">Get your code</h3>
                    <Tabs defaultValue="script" className="w-full">
                      <TabsList className="grid w-full grid-cols-3 mb-4">
                        <TabsTrigger value="script">JavaScript</TabsTrigger>
                        <TabsTrigger value="iframe">HTML</TabsTrigger>
                        <TabsTrigger value="iframe-react">React</TabsTrigger>
                      </TabsList>
                      <TabsContent value="script">
                        <ScriptEmbed embedCode={scriptEmbedCode} />
                      </TabsContent>
                      <TabsContent value="iframe">
                        <IFrameEmbed embedCode={iframeEmbedCode} />
                      </TabsContent>
                      <TabsContent value="iframe-react">
                        <ReactEmbed embedCode={iframeReactCode} />
                      </TabsContent>
                    </Tabs>
                    <div className="mt-6">
                      <LeadConversionsSettings genie={genie} />
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Appearance tab — full customization side-by-side with a live preview.
            On mobile both panels stack and flow into the drawer's own scroll
            container instead of each fighting for a slice of a fixed height. */}
            <TabsContent value="appearance" className="flex-1 mt-0 lg:overflow-hidden">
              <div className="grid grid-cols-1 lg:h-full lg:grid-cols-[minmax(0,1fr)_360px] lg:overflow-hidden">
                <div className="lg:overflow-y-auto p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-700">Customize your widget</h3>
                    <button
                      onClick={toggleAllSections}
                      className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
                    >
                      {allSectionsOpen ? "Collapse all" : "Expand all"}
                    </button>
                  </div>

                  {styleAppliedFromImage && (
                    <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
                      <div className="flex items-start gap-2">
                        <Sparkles className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-blue-900">
                          Applied from your image — review the highlighted sections below, then Save.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setStyleAppliedFromImage(false)}
                        className="text-blue-400 hover:text-blue-600 flex-shrink-0"
                        aria-label="Dismiss"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}

                  <div className="space-y-4">
                    <CollapsibleSection
                      title="Brand"
                      isOpen={openSections.has("brand")}
                      onToggle={() => toggleSection("brand")}
                    >
                      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 mb-5">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-900">
                          This logo and these colors are this genie's overall brand — changing them here updates the
                          avatar, QR codes, email signature, and every other surface, not just this widget.
                        </p>
                      </div>

                      <div className="flex items-center gap-4 mb-6">
                        <GenieAvatar
                          color={brandPrimaryColor}
                          secondaryColor={brandSecondaryColor}
                          gradientEnabled={brandGradientEnabled}
                          avatarBackgroundOpacity={genie.branding?.avatarBackgroundOpacity}
                          customAvatarUrl={brandLogoUrl}
                          hasCustomBranding={true}
                          size={64}
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{genie.name}</p>
                          <p className="text-xs text-gray-500">Current genie logo and brand colors</p>
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Genie Logo</Label>
                        <div className="mt-2">
                          <EnhancedImageUpload
                            value={brandLogoUrl}
                            onChange={(url) => setBrandLogoUrl(url)}
                            onUploadComplete={handleBrandLogoUploadComplete}
                            bucket="genie-images"
                            showColorExtraction={true}
                            showBackgroundRemoval={true}
                            squarePad={true}
                          />
                        </div>
                      </div>

                      <div className="mt-6 pt-6 border-t space-y-4">
                        <div>
                          <Label className="text-xs text-gray-500">Primary Color</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <ColorSwatchButton
                              value={brandPrimaryColor}
                              onChange={setBrandPrimaryColor}
                              label="Primary color"
                            />
                            <BrandColorQuickPicks
                              colors={brandExtractedPalette?.palette || []}
                              onPick={setBrandPrimaryColor}
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Gradient Color</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <ColorSwatchButton
                              value={brandSecondaryColor || "#000000"}
                              onChange={(value) => {
                                setBrandSecondaryColor(value);
                                setBrandGradientEnabled(true);
                              }}
                              label="Gradient color"
                            />
                            <BrandColorQuickPicks
                              colors={brandExtractedPalette?.palette || []}
                              onPick={(color) => {
                                setBrandSecondaryColor(color);
                                setBrandGradientEnabled(true);
                              }}
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Accent Color</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <ColorSwatchButton
                              value={brandAccentColor || "#000000"}
                              onChange={setBrandAccentColor}
                              label="Accent color"
                            />
                            <BrandColorQuickPicks
                              colors={brandExtractedPalette?.palette || []}
                              onPick={setBrandAccentColor}
                            />
                          </div>
                        </div>
                      </div>

                      {hasUnsavedBrandChanges && (
                        <div className="mt-6 pt-6 border-t flex justify-end">
                          <Button onClick={handleSaveBrandSettings} disabled={updateAgent.isPending}>
                            {updateAgent.isPending ? "Saving..." : "Save Brand Settings"}
                          </Button>
                        </div>
                      )}

                      <div className="mt-6 pt-6 border-t">
                        <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                          <Sparkles className="w-3.5 h-3.5" />
                          Style From an Image
                        </Label>
                        <p className="text-xs text-gray-500 mt-1 mb-3">
                          Upload a screenshot, mood board, or a widget you like — AI proposes matching colors and widget
                          style for you to review. This image is a style reference only and is never saved as your logo.
                        </p>
                        <label
                          className={cn(
                            "flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 px-4 py-6 text-sm text-gray-500 hover:text-gray-700 cursor-pointer transition-colors",
                            isAnalyzingStyleImage && "opacity-60 pointer-events-none",
                          )}
                        >
                          {isAnalyzingStyleImage ? (
                            <>
                              <Sparkles className="w-4 h-4 animate-pulse" />
                              Analyzing image...
                            </>
                          ) : (
                            <>
                              <ImagePlus className="w-4 h-4" />
                              Click to upload a style reference image
                            </>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={isAnalyzingStyleImage}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) void handleStyleReferenceImage(file);
                              e.target.value = "";
                            }}
                          />
                        </label>
                      </div>
                    </CollapsibleSection>

                    <CollapsibleSection
                      title="Style"
                      isOpen={openSections.has("style")}
                      onToggle={() => toggleSection("style")}
                    >
                      <p className="text-sm text-gray-500 mb-6">Pick how your widget appears on your website</p>
                      <WidgetStyleCatalog
                        selectedTemplate={selectedTemplate}
                        onSelectTemplate={setSelectedTemplate}
                        branding={genie.branding}
                      />

                      <div className="mt-6 pt-6 border-t">
                        <Label
                          htmlFor="widgetButtonLabel"
                          className="text-xs font-medium text-gray-400 uppercase tracking-wider"
                        >
                          Button Label To Open The Widget
                        </Label>
                        <Input
                          id="widgetButtonLabel"
                          value={buttonLabel}
                          onChange={(e) => setButtonLabel(e.target.value.slice(0, BUTTON_LABEL_MAX_LENGTH))}
                          placeholder="Need help? Talk to our assistant"
                          maxLength={BUTTON_LABEL_MAX_LENGTH}
                          className="mt-2"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Shown on styles with a text label (pill, banner, side tab). Leave blank to use the style's
                          default.
                        </p>
                      </div>
                    </CollapsibleSection>

                    <CollapsibleSection
                      title="Popup Size"
                      isOpen={openSections.has("size")}
                      onToggle={() => toggleSection("size")}
                    >
                      <div className="grid grid-cols-2 gap-3">
                        {POPUP_SIZES.map((size) => {
                          const isSelected = selectedSize === size.id;
                          return (
                            <button
                              key={size.id}
                              onClick={() => setSelectedSize(size.id)}
                              className={cn(
                                "relative text-left rounded-xl px-4 py-3 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2",
                                isSelected
                                  ? "ring-2 ring-genie-primary focus:ring-genie-primary bg-genie-primary/5"
                                  : "ring-1 ring-gray-200 hover:ring-gray-300 focus:ring-gray-300 bg-white",
                              )}
                            >
                              {isSelected && (
                                <span className="absolute top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-genie-primary">
                                  <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                                </span>
                              )}
                              <p
                                className={cn(
                                  "font-medium text-sm",
                                  isSelected ? "text-genie-primary" : "text-gray-900",
                                )}
                              >
                                {size.label}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">{size.description}</p>
                              <p className="text-[10px] text-gray-400 mt-1 font-mono">{size.dimensions}</p>
                            </button>
                          );
                        })}
                      </div>
                    </CollapsibleSection>

                    <CollapsibleSection
                      title="Screen Position"
                      isOpen={openSections.has("position")}
                      onToggle={() => toggleSection("position")}
                    >
                      <div className="grid grid-cols-2 gap-3">
                        {POPUP_POSITIONS.map((position) => {
                          const isSelected = selectedPosition === position.id;
                          const Icon = position.icon;
                          return (
                            <button
                              key={position.id}
                              onClick={() => setSelectedPosition(position.id)}
                              className={cn(
                                "relative flex items-center gap-2 text-left rounded-xl px-4 py-3 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2",
                                isSelected
                                  ? "ring-2 ring-genie-primary focus:ring-genie-primary bg-genie-primary/5"
                                  : "ring-1 ring-gray-200 hover:ring-gray-300 focus:ring-gray-300 bg-white",
                              )}
                            >
                              <Icon className={cn("h-4 w-4", isSelected ? "text-genie-primary" : "text-gray-400")} />
                              <p
                                className={cn(
                                  "font-medium text-sm",
                                  isSelected ? "text-genie-primary" : "text-gray-900",
                                )}
                              >
                                {position.label}
                              </p>
                              {isSelected && (
                                <span className="absolute top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-genie-primary">
                                  <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </CollapsibleSection>

                    <CollapsibleSection
                      title="Content"
                      isOpen={openSections.has("content")}
                      onToggle={() => toggleSection("content")}
                    >
                      <div>
                        <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Background</Label>
                        <div className="flex items-center gap-2 mt-2">
                          {(["solid", "gradient", "pattern"] as const).map((type) => {
                            const isSelected = (background?.type ?? "solid") === type;
                            return (
                              <button
                                key={type}
                                type="button"
                                onClick={() =>
                                  setBackground((prev) => ({
                                    type,
                                    color: prev?.color || backgroundColor || "#FFFFFF",
                                    color2: prev?.color2,
                                    angle: prev?.angle ?? 135,
                                    patternId: prev?.patternId ?? "dots",
                                  }))
                                }
                                className={cn(
                                  "px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-all",
                                  isSelected
                                    ? "bg-genie-primary/10 text-genie-primary ring-1 ring-genie-primary"
                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                                )}
                              >
                                {type}
                              </button>
                            );
                          })}
                        </div>

                        {(!background || background.type === "solid") && (
                          <div className="flex items-center gap-2 mt-3">
                            <ColorSwatchButton
                              value={backgroundColor || "#FFFFFF"}
                              onChange={(value) => {
                                setBackgroundColor(value);
                                setBackground(null);
                              }}
                              label="Background color"
                            />
                            <Input
                              type="text"
                              value={backgroundColor}
                              onChange={(e) => {
                                setBackgroundColor(e.target.value);
                                setBackground(null);
                              }}
                              placeholder="Default (white)"
                              className="flex-1"
                            />
                            <BrandColorQuickPicks
                              colors={backgroundSwatches}
                              onPick={(color) => {
                                setBackgroundColor(color);
                                setBackground(null);
                              }}
                            />
                          </div>
                        )}

                        {background?.type === "gradient" && (
                          <div className="mt-3 space-y-3">
                            <div>
                              <Label className="text-xs text-gray-500">Start color</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <ColorSwatchButton
                                  value={background.color || "#FFFFFF"}
                                  onChange={(value) => setBackground({ ...background, color: value })}
                                  label="Gradient start"
                                />
                                <BrandColorQuickPicks
                                  colors={backgroundSwatches}
                                  onPick={(color) => setBackground({ ...background, color })}
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">End color</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <ColorSwatchButton
                                  value={background.color2 || "#FFFFFF"}
                                  onChange={(value) => setBackground({ ...background, color2: value })}
                                  label="Gradient end"
                                />
                                <BrandColorQuickPicks
                                  colors={backgroundSwatches}
                                  onPick={(color) => setBackground({ ...background, color2: color })}
                                />
                              </div>
                            </div>
                            <p className="text-xs text-gray-500">Two-color gradient (135°)</p>
                          </div>
                        )}

                        {background?.type === "pattern" && (
                          <div className="mt-3 space-y-3">
                            <div className="flex items-center gap-2">
                              {(["dots", "grid", "diagonal-lines"] as const).map((patternId) => (
                                <button
                                  key={patternId}
                                  type="button"
                                  onClick={() => setBackground({ ...background, patternId })}
                                  className={cn(
                                    "w-14 h-11 rounded-md border-2 transition-all flex-shrink-0",
                                    background.patternId === patternId
                                      ? "border-genie-primary ring-2 ring-genie-primary/30"
                                      : "border-gray-300 hover:border-gray-400",
                                  )}
                                  style={widgetBackgroundToCss({ ...background, patternId }, "#FFFFFF")}
                                  title={patternId}
                                />
                              ))}
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Base color</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <ColorSwatchButton
                                  value={background.color || "#FFFFFF"}
                                  onChange={(value) => setBackground({ ...background, color: value })}
                                  label="Base color"
                                />
                                <BrandColorQuickPicks
                                  colors={backgroundSwatches}
                                  onPick={(color) => setBackground({ ...background, color })}
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Pattern Size</Label>
                              <div className="flex items-center gap-3 mt-1">
                                <input
                                  type="range"
                                  min={4}
                                  max={100}
                                  step={2}
                                  value={background.patternSize ?? DEFAULT_PATTERN_SIZE[background.patternId ?? "dots"]}
                                  onChange={(e) =>
                                    setBackground({ ...background, patternSize: Number(e.target.value) })
                                  }
                                  className="flex-1"
                                />
                                <span className="text-sm text-gray-600 w-10 text-right">
                                  {background.patternSize ?? DEFAULT_PATTERN_SIZE[background.patternId ?? "dots"]}px
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="mt-6 pt-6 border-t flex items-center justify-between">
                        <div>
                          <Label htmlFor="hideWelcomeText">Hide Description</Label>
                          <p className="text-sm text-gray-500">
                            Remove the subtitle line shown under the genie's name in the header
                          </p>
                        </div>
                        <Switch id="hideWelcomeText" checked={hideWelcomeText} onCheckedChange={setHideWelcomeText} />
                      </div>

                      <div className="mt-6 pt-6 border-t flex items-center justify-between">
                        <div>
                          <Label htmlFor="hideWelcomeMessage">Hide Welcome Message</Label>
                          <p className="text-sm text-gray-500">
                            Remove the greeting line shown above the Talk/Chat buttons
                          </p>
                        </div>
                        <Switch
                          id="hideWelcomeMessage"
                          checked={hideWelcomeMessage}
                          onCheckedChange={setHideWelcomeMessage}
                        />
                      </div>

                      {canEditAttribution && (
                        <div className="mt-6 pt-6 border-t flex items-center justify-between">
                          <div>
                            <Label htmlFor="hideAttribution">Hide "Powered by Help Genie"</Label>
                            <p className="text-sm text-gray-500">Remove the attribution shown in the popup widget</p>
                            <p className="text-xs text-gray-400 mt-1">Admin only</p>
                          </div>
                          <Switch id="hideAttribution" checked={hideAttribution} onCheckedChange={setHideAttribution} />
                        </div>
                      )}
                    </CollapsibleSection>

                    <CollapsibleSection
                      title="Caption Bubble"
                      isOpen={openSections.has("caption")}
                      onToggle={() => toggleSection("caption")}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="captionEnabled">Show caption bubble</Label>
                          <p className="text-sm text-gray-500">
                            A speech-bubble hint next to the closed widget button inviting visitors to chat
                          </p>
                        </div>
                        <Switch
                          id="captionEnabled"
                          checked={caption?.enabled ?? false}
                          onCheckedChange={(enabled) => setCaption({ ...caption, enabled })}
                        />
                      </div>

                      {caption?.enabled && (
                        <>
                          <div className="mt-6 pt-6 border-t">
                            <Label htmlFor="captionText">Caption text</Label>
                            <Input
                              id="captionText"
                              type="text"
                              value={caption?.text ?? ""}
                              onChange={(e) => setCaption({ ...caption, text: e.target.value })}
                              placeholder="Got a question? Just ask"
                              maxLength={BUTTON_LABEL_MAX_LENGTH}
                              className="mt-2"
                            />
                          </div>

                          <div className="mt-6 pt-6 border-t">
                            <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Size</Label>
                            <div className="flex items-center gap-2 mt-2">
                              {(["small", "medium", "large"] as const).map((size) => {
                                const isSelected = (caption?.size ?? "medium") === size;
                                return (
                                  <button
                                    key={size}
                                    type="button"
                                    onClick={() => setCaption({ ...caption, size })}
                                    className={cn(
                                      "px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-all",
                                      isSelected
                                        ? "bg-genie-primary/10 text-genie-primary ring-1 ring-genie-primary"
                                        : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                                    )}
                                  >
                                    {size}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          <div className="mt-6 pt-6 border-t grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs text-gray-500">Background color</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <ColorSwatchButton
                                  value={caption?.backgroundColor || "#FFFFFF"}
                                  onChange={(value) => setCaption({ ...caption, backgroundColor: value })}
                                  label="Caption background"
                                />
                                <Input
                                  type="text"
                                  value={caption?.backgroundColor ?? ""}
                                  onChange={(e) => setCaption({ ...caption, backgroundColor: e.target.value })}
                                  placeholder="Default (white)"
                                  className="flex-1"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Text color</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <ColorSwatchButton
                                  value={caption?.textColor || "#1F2937"}
                                  onChange={(value) => setCaption({ ...caption, textColor: value })}
                                  label="Caption text color"
                                />
                                <Input
                                  type="text"
                                  value={caption?.textColor ?? ""}
                                  onChange={(e) => setCaption({ ...caption, textColor: e.target.value })}
                                  placeholder="Default (dark gray)"
                                  className="flex-1"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="mt-6 pt-6 border-t">
                            <Label className="text-xs text-gray-500">Delay before showing</Label>
                            <div className="flex items-center gap-3 mt-1">
                              <input
                                type="range"
                                min={0}
                                max={20000}
                                step={500}
                                value={caption?.delayMs ?? 4000}
                                onChange={(e) => setCaption({ ...caption, delayMs: Number(e.target.value) })}
                                className="flex-1"
                              />
                              <span className="text-sm text-gray-600 w-14 text-right">
                                {((caption?.delayMs ?? 4000) / 1000).toFixed(1)}s
                              </span>
                            </div>
                          </div>

                          <div className="mt-6 pt-6 border-t">
                            <Label className="text-xs text-gray-500">Auto-hide after</Label>
                            <div className="flex items-center gap-3 mt-1">
                              <input
                                type="range"
                                min={0}
                                max={20000}
                                step={500}
                                value={caption?.autoHideMs ?? 8000}
                                onChange={(e) => setCaption({ ...caption, autoHideMs: Number(e.target.value) })}
                                className="flex-1"
                              />
                              <span className="text-sm text-gray-600 w-20 text-right">
                                {(caption?.autoHideMs ?? 8000) === 0
                                  ? "Never"
                                  : `${((caption?.autoHideMs ?? 8000) / 1000).toFixed(1)}s`}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </CollapsibleSection>

                    <CollapsibleSection
                      title="Avatar Photo"
                      isOpen={openSections.has("avatar")}
                      onToggle={() => toggleSection("avatar")}
                    >
                      <p className="text-sm text-gray-500 mb-4">
                        Upload a photo to fill the big central avatar circle on the popup's start screen. This does not
                        change the genie's logo shown in the header — set that from the genie's branding instead.
                      </p>
                      <EnhancedImageUpload
                        value={avatarImageUrl}
                        onChange={(url) => setAvatarImageUrl(url)}
                        bucket="genie-images"
                        showColorExtraction={false}
                        showBackgroundRemoval={false}
                        squarePad={false}
                      />

                      {avatarImageUrl && (genie.branding?.logoUrl || genie.branding?.customAvatarUrl) && (
                        <div className="mt-6 pt-6 border-t flex items-center justify-between">
                          <div>
                            <Label htmlFor="avatarShowLogoOverlay">Show Logo on Top</Label>
                            <p className="text-sm text-gray-500">
                              Show the genie's logo as a small badge over the uploaded photo
                            </p>
                          </div>
                          <Switch
                            id="avatarShowLogoOverlay"
                            checked={avatarShowLogoOverlay}
                            onCheckedChange={setAvatarShowLogoOverlay}
                          />
                        </div>
                      )}

                      <div className="mt-6 pt-6 border-t">
                        <Label
                          htmlFor="avatarSizeScale"
                          className="text-xs font-medium text-gray-400 uppercase tracking-wider"
                        >
                          Avatar Size
                        </Label>
                        <div className="flex items-center gap-3 mt-2">
                          <input
                            id="avatarSizeScale"
                            type="range"
                            min={0.4}
                            max={2.5}
                            step={0.05}
                            value={avatarSizeScale}
                            onChange={(e) => setAvatarSizeScale(Number(e.target.value))}
                            className="flex-1"
                          />
                          <span className="text-sm text-gray-600 w-12 text-right">
                            {Math.round(avatarSizeScale * 100)}%
                          </span>
                          {avatarSizeScale !== 1 && (
                            <button
                              type="button"
                              onClick={() => setAvatarSizeScale(1)}
                              className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                            >
                              Reset
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Scales the avatar circle up or down. Applies to both the default genie avatar and the uploaded
                          photo above.
                        </p>
                      </div>
                    </CollapsibleSection>

                    <CollapsibleSection
                      title="Header"
                      isOpen={openSections.has("header")}
                      onToggle={() => toggleSection("header")}
                    >
                      <div>
                        <Label
                          htmlFor="headerLogoSize"
                          className="text-xs font-medium text-gray-400 uppercase tracking-wider"
                        >
                          Header Logo Size
                        </Label>
                        <div className="flex items-center gap-3 mt-2">
                          <input
                            id="headerLogoSize"
                            type="range"
                            min={20}
                            max={96}
                            step={2}
                            value={headerLogoSize}
                            onChange={(e) => setHeaderLogoSize(Number(e.target.value))}
                            className="flex-1"
                          />
                          <span className="text-sm text-gray-600 w-10 text-right">{headerLogoSize}px</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Size of the genie mark shown above the avatar and chat screens.
                        </p>
                      </div>

                      <div className="mt-6 pt-6 border-t flex items-center justify-between">
                        <div>
                          <Label htmlFor="headerTransparent">Transparent Header</Label>
                          <p className="text-sm text-gray-500">Let the popup background show through the header bar</p>
                        </div>
                        <Switch
                          id="headerTransparent"
                          checked={!!headerStyle?.transparent}
                          onCheckedChange={(checked) =>
                            setHeaderStyle((prev) => ({
                              ...prev,
                              color: prev?.color,
                              color2: prev?.color2,
                              transparent: checked,
                            }))
                          }
                        />
                      </div>

                      {!headerStyle?.transparent && (
                        <div className="mt-6 pt-6 border-t">
                          <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Header Color
                          </Label>
                          <div className="mt-2 space-y-3">
                            <div>
                              <Label className="text-xs text-gray-500">Start color</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <ColorSwatchButton
                                  value={headerStyle?.color || primaryColor}
                                  onChange={(value) => setHeaderStyle({ ...headerStyle, color: value })}
                                  label="Header color start"
                                />
                                <BrandColorQuickPicks
                                  colors={backgroundSwatches}
                                  onPick={(color) => setHeaderStyle({ ...headerStyle, color })}
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">End color</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <ColorSwatchButton
                                  value={headerStyle?.color2 || primaryColor}
                                  onChange={(value) => setHeaderStyle({ ...headerStyle, color2: value })}
                                  label="Header color end"
                                />
                                <BrandColorQuickPicks
                                  colors={backgroundSwatches}
                                  onPick={(color) => setHeaderStyle({ ...headerStyle, color2: color })}
                                />
                              </div>
                            </div>
                            {headerStyle && (
                              <button
                                type="button"
                                onClick={() => setHeaderStyle(null)}
                                className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                              >
                                Reset
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">Gradient from the first color to the second.</p>
                        </div>
                      )}
                    </CollapsibleSection>

                    <CollapsibleSection
                      title="Buttons"
                      isOpen={openSections.has("colors")}
                      onToggle={() => toggleSection("colors")}
                    >
                      <p className="text-sm text-gray-500 mb-4">Talk button</p>
                      <div className="flex items-center gap-2">
                        {(["rounded", "square", "pill"] as const).map((shapeOption) => (
                          <button
                            key={shapeOption}
                            type="button"
                            onClick={() => setCtaShape(shapeOption)}
                            className={cn(
                              "px-3 py-1.5 text-sm font-medium capitalize transition-all",
                              shapeOption === "pill"
                                ? "rounded-full"
                                : shapeOption === "rounded"
                                  ? "rounded-lg"
                                  : "rounded-sm",
                              ctaShape === shapeOption
                                ? "bg-genie-primary/10 text-genie-primary ring-1 ring-genie-primary"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                            )}
                          >
                            {shapeOption}
                          </button>
                        ))}
                      </div>

                      <div className="mt-4">
                        <Label htmlFor="ctaSizeScale" className="text-xs text-gray-500">
                          Size
                        </Label>
                        <div className="flex items-center gap-3 mt-1">
                          <input
                            id="ctaSizeScale"
                            type="range"
                            min={0.7}
                            max={1.6}
                            step={0.01}
                            value={ctaButton?.sizeScale ?? 1}
                            onChange={(e) => setCtaButton({ ...ctaButton, sizeScale: Number(e.target.value) })}
                            className="flex-1"
                          />
                          <span className="text-sm text-gray-600 w-12 text-right">
                            {Math.round((ctaButton?.sizeScale ?? 1) * 100)}%
                          </span>
                          {ctaButton?.sizeScale != null && ctaButton.sizeScale !== 1 && (
                            <button
                              type="button"
                              onClick={() => setCtaButton({ ...ctaButton, sizeScale: 1 })}
                              className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                            >
                              Reset
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 space-y-4">
                        <div>
                          <Label className="text-xs text-gray-500">Color</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <ColorSwatchButton
                              value={ctaButton?.color || primaryColor}
                              onChange={(value) => setCtaButton({ ...ctaButton, color: value })}
                              label="CTA color"
                            />
                            <BrandColorQuickPicks
                              colors={backgroundSwatches}
                              onPick={(color) => setCtaButton({ ...ctaButton, color })}
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Gradient end (optional)</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <ColorSwatchButton
                              value={ctaButton?.color2 || primaryColor}
                              onChange={(value) => setCtaButton({ ...ctaButton, color2: value })}
                              label="CTA gradient end"
                            />
                            <BrandColorQuickPicks
                              colors={backgroundSwatches}
                              onPick={(color) => setCtaButton({ ...ctaButton, color2: color })}
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Text color</Label>
                          <div className="mt-1">
                            <ColorSwatchButton
                              value={ctaButton?.textColor || "#FFFFFF"}
                              onChange={(value) => setCtaButton({ ...ctaButton, textColor: value })}
                              label="CTA text color"
                            />
                          </div>
                        </div>
                        {ctaButton && (
                          <button
                            type="button"
                            onClick={() => setCtaButton(null)}
                            className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                          >
                            Reset
                          </button>
                        )}
                      </div>

                      <div className="mt-6 pt-6 border-t">
                        <p className="text-sm text-gray-500 mb-4">Chat button</p>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="chatSizeScale" className="text-xs text-gray-500">
                              Size
                            </Label>
                            <div className="flex items-center gap-3 mt-1">
                              <input
                                id="chatSizeScale"
                                type="range"
                                min={0.7}
                                max={1.6}
                                step={0.01}
                                value={chatButton?.sizeScale ?? 1}
                                onChange={(e) => setChatButton({ ...chatButton, sizeScale: Number(e.target.value) })}
                                className="flex-1"
                              />
                              <span className="text-sm text-gray-600 w-12 text-right">
                                {Math.round((chatButton?.sizeScale ?? 1) * 100)}%
                              </span>
                              {chatButton?.sizeScale != null && chatButton.sizeScale !== 1 && (
                                <button
                                  type="button"
                                  onClick={() => setChatButton({ ...chatButton, sizeScale: 1 })}
                                  className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                                >
                                  Reset
                                </button>
                              )}
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Border &amp; text color</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <ColorSwatchButton
                                value={chatButton?.color || "#D1D5DB"}
                                onChange={(value) => setChatButton({ ...chatButton, color: value })}
                                label="Chat button color"
                              />
                              <BrandColorQuickPicks
                                colors={backgroundSwatches}
                                onPick={(color) => setChatButton({ ...chatButton, color })}
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Background (optional)</Label>
                            <p className="text-xs text-gray-500 mt-0.5 mb-1">
                              Leave unset to keep it outlined/transparent.
                            </p>
                            <div className="flex items-center gap-2">
                              <ColorSwatchButton
                                value={chatButton?.backgroundColor || "#FFFFFF"}
                                onChange={(value) => setChatButton({ ...chatButton, backgroundColor: value })}
                                label="Chat button background"
                              />
                              {chatButton?.backgroundColor && (
                                <button
                                  type="button"
                                  onClick={() => setChatButton({ ...chatButton, backgroundColor: null })}
                                  className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                          </div>
                          {chatButton && (
                            <button
                              type="button"
                              onClick={() => setChatButton(null)}
                              className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                            >
                              Reset
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="mt-6 pt-6 border-t">
                        <p className="text-sm text-gray-500 mb-4">Action buttons (mic, chat, mute, etc.)</p>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="actionSizeScale" className="text-xs text-gray-500">
                              Size
                            </Label>
                            <div className="flex items-center gap-3 mt-1">
                              <input
                                id="actionSizeScale"
                                type="range"
                                min={0.7}
                                max={1.6}
                                step={0.01}
                                value={actionButtons?.sizeScale ?? 1}
                                onChange={(e) =>
                                  setActionButtons({ ...actionButtons, sizeScale: Number(e.target.value) })
                                }
                                className="flex-1"
                              />
                              <span className="text-sm text-gray-600 w-12 text-right">
                                {Math.round((actionButtons?.sizeScale ?? 1) * 100)}%
                              </span>
                              {actionButtons?.sizeScale != null && actionButtons.sizeScale !== 1 && (
                                <button
                                  type="button"
                                  onClick={() => setActionButtons({ ...actionButtons, sizeScale: 1 })}
                                  className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                                >
                                  Reset
                                </button>
                              )}
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Idle background</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <ColorSwatchButton
                                value={actionButtons?.idleBackgroundColor || primaryColor}
                                onChange={(value) => setActionButtons({ ...actionButtons, idleBackgroundColor: value })}
                                label="Idle background"
                              />
                              <BrandColorQuickPicks
                                colors={backgroundSwatches}
                                onPick={(color) => setActionButtons({ ...actionButtons, idleBackgroundColor: color })}
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Active background</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <ColorSwatchButton
                                value={actionButtons?.activeBackgroundColor || primaryColor}
                                onChange={(value) =>
                                  setActionButtons({ ...actionButtons, activeBackgroundColor: value })
                                }
                                label="Active background"
                              />
                              <BrandColorQuickPicks
                                colors={backgroundSwatches}
                                onPick={(color) => setActionButtons({ ...actionButtons, activeBackgroundColor: color })}
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Icon color</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <ColorSwatchButton
                                value={actionButtons?.iconColor || primaryColor}
                                onChange={(value) => setActionButtons({ ...actionButtons, iconColor: value })}
                                label="Icon color"
                              />
                              <BrandColorQuickPicks
                                colors={backgroundSwatches}
                                onPick={(color) => setActionButtons({ ...actionButtons, iconColor: color })}
                              />
                            </div>
                          </div>
                          {actionButtons && (
                            <button
                              type="button"
                              onClick={() => setActionButtons(null)}
                              className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                            >
                              Reset
                            </button>
                          )}
                        </div>
                      </div>
                    </CollapsibleSection>

                    <CollapsibleSection
                      title="Chat View"
                      isOpen={openSections.has("chat")}
                      onToggle={() => toggleSection("chat")}
                    >
                      <p className="text-sm text-gray-500 mb-4">
                        Controls for the "Type instead" chat view. The background always matches the popup background
                        set in Content.
                      </p>

                      <div>
                        <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Input Background
                        </Label>
                        <div className="flex items-center gap-2 mt-2">
                          <ColorSwatchButton
                            value={chatStyle?.inputBackgroundColor || "#FFFFFF"}
                            onChange={(value) => setChatStyle({ ...chatStyle, inputBackgroundColor: value })}
                            label="Input background"
                          />
                          <BrandColorQuickPicks
                            colors={backgroundSwatches}
                            onPick={(color) => setChatStyle({ ...chatStyle, inputBackgroundColor: color })}
                          />
                          {chatStyle?.inputBackgroundColor && (
                            <button
                              type="button"
                              onClick={() => setChatStyle({ ...chatStyle, inputBackgroundColor: null })}
                              className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                            >
                              Reset
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="mt-6 pt-6 border-t flex items-center justify-between">
                        <div>
                          <Label htmlFor="chatHideBorder">Show Border</Label>
                          <p className="text-sm text-gray-500">Gradient border around the input pill</p>
                        </div>
                        <Switch
                          id="chatHideBorder"
                          checked={!chatStyle?.hideBorder}
                          onCheckedChange={(checked) => setChatStyle({ ...chatStyle, hideBorder: !checked })}
                        />
                      </div>

                      {!chatStyle?.hideBorder && (
                        <div className="mt-4">
                          <Label className="text-xs text-gray-500">Border Color</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <ColorSwatchButton
                              value={chatStyle?.borderColor || primaryColor}
                              onChange={(value) => setChatStyle({ ...chatStyle, borderColor: value })}
                              label="Border color"
                            />
                            <span className="text-xs text-gray-400">to</span>
                            <ColorSwatchButton
                              value={chatStyle?.borderColor2 || primaryColor}
                              onChange={(value) => setChatStyle({ ...chatStyle, borderColor2: value })}
                              label="Border gradient end"
                            />
                            <BrandColorQuickPicks
                              colors={backgroundSwatches}
                              onPick={(color) => setChatStyle({ ...chatStyle, borderColor: color })}
                            />
                            {(chatStyle?.borderColor || chatStyle?.borderColor2) && (
                              <button
                                type="button"
                                onClick={() => setChatStyle({ ...chatStyle, borderColor: null, borderColor2: null })}
                                className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                              >
                                Reset
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="mt-6 pt-6 border-t">
                        <Label className="text-xs text-gray-500">Placeholder Text Color</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <ColorSwatchButton
                            value={chatStyle?.placeholderColor || "#9CA3AF"}
                            onChange={(value) => setChatStyle({ ...chatStyle, placeholderColor: value })}
                            label="Placeholder color"
                          />
                          <BrandColorQuickPicks
                            colors={backgroundSwatches}
                            onPick={(color) => setChatStyle({ ...chatStyle, placeholderColor: color })}
                          />
                          {chatStyle?.placeholderColor && (
                            <button
                              type="button"
                              onClick={() => setChatStyle({ ...chatStyle, placeholderColor: null })}
                              className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                            >
                              Reset
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="mt-6 pt-6 border-t">
                        <Label className="text-xs text-gray-500">Plus / Attach Button Color</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <ColorSwatchButton
                            value={chatStyle?.plusButtonColor || primaryColor}
                            onChange={(value) => setChatStyle({ ...chatStyle, plusButtonColor: value })}
                            label="Plus button color"
                          />
                          <BrandColorQuickPicks
                            colors={backgroundSwatches}
                            onPick={(color) => setChatStyle({ ...chatStyle, plusButtonColor: color })}
                          />
                          {chatStyle?.plusButtonColor && (
                            <button
                              type="button"
                              onClick={() => setChatStyle({ ...chatStyle, plusButtonColor: null })}
                              className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                            >
                              Reset
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="mt-6 pt-6 border-t flex items-center justify-between">
                        <div>
                          <Label htmlFor="chatSendButtonUseActionStyle">Match Action Buttons Style</Label>
                          <p className="text-sm text-gray-500">
                            Use the Action Buttons colors (set in Colors) for the send button, instead of the default
                            brand gradient
                          </p>
                        </div>
                        <Switch
                          id="chatSendButtonUseActionStyle"
                          checked={!!chatStyle?.sendButtonUseActionStyle}
                          onCheckedChange={(checked) =>
                            setChatStyle({ ...chatStyle, sendButtonUseActionStyle: checked })
                          }
                        />
                      </div>

                      <div className="mt-6 pt-6 border-t space-y-5">
                        <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Empty State
                        </Label>
                        <p className="text-xs text-gray-500 -mt-3">
                          The icon, title, and subtitle shown before any messages exist.
                        </p>
                        {(chatStyle?.elementOrder ?? EMPTY_STATE_ELEMENTS).map((el, index, order) => {
                          const isHidden = (chatStyle?.hiddenElements ?? []).includes(el);
                          const align = chatStyle?.elementAlign?.[el] ?? "center";

                          const moveElement = (direction: -1 | 1) => {
                            const currentOrder = chatStyle?.elementOrder ?? EMPTY_STATE_ELEMENTS;
                            const newOrder = [...currentOrder];
                            const targetIndex = index + direction;
                            if (targetIndex < 0 || targetIndex >= newOrder.length) return;
                            [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
                            setChatStyle({ ...chatStyle, elementOrder: newOrder });
                          };

                          const toggleHidden = () => {
                            const hiddenSet = new Set(chatStyle?.hiddenElements ?? []);
                            if (hiddenSet.has(el)) hiddenSet.delete(el);
                            else hiddenSet.add(el);
                            setChatStyle({ ...chatStyle, hiddenElements: Array.from(hiddenSet) });
                          };

                          const setAlign = (value: "left" | "center" | "right") => {
                            setChatStyle({
                              ...chatStyle,
                              elementAlign: { ...chatStyle?.elementAlign, [el]: value },
                            });
                          };

                          return (
                            <div key={el} className="rounded-lg border border-gray-200 p-3">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700">{EMPTY_STATE_LABELS[el]}</span>
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => moveElement(-1)}
                                      disabled={index === 0}
                                      className="px-1.5 py-0.5 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-500"
                                      aria-label={`Move ${el} up`}
                                    >
                                      ↑
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => moveElement(1)}
                                      disabled={index === order.length - 1}
                                      className="px-1.5 py-0.5 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-500"
                                      aria-label={`Move ${el} down`}
                                    >
                                      ↓
                                    </button>
                                  </div>
                                  <Switch
                                    checked={!isHidden}
                                    onCheckedChange={toggleHidden}
                                    aria-label={`Show ${el}`}
                                  />
                                </div>
                              </div>

                              {!isHidden && (
                                <div className="mt-3 space-y-3">
                                  <div className="flex items-center gap-2">
                                    {(["left", "center", "right"] as const).map((alignOption) => (
                                      <button
                                        key={alignOption}
                                        type="button"
                                        onClick={() => setAlign(alignOption)}
                                        className={cn(
                                          "px-2.5 py-1 rounded-lg text-xs font-medium capitalize transition-all",
                                          align === alignOption
                                            ? "bg-genie-primary/10 text-genie-primary ring-1 ring-genie-primary"
                                            : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                                        )}
                                      >
                                        {alignOption}
                                      </button>
                                    ))}
                                  </div>

                                  {el === "title" && (
                                    <Input
                                      type="text"
                                      value={chatStyle?.titleText || ""}
                                      onChange={(e) =>
                                        setChatStyle({ ...chatStyle, titleText: e.target.value || null })
                                      }
                                      placeholder="Ask {genie name} anything"
                                    />
                                  )}
                                  {el === "subtitle" && (
                                    <Input
                                      type="text"
                                      value={chatStyle?.subtitleText || ""}
                                      onChange={(e) =>
                                        setChatStyle({ ...chatStyle, subtitleText: e.target.value || null })
                                      }
                                      placeholder="Type a question, or just speak — the conversation flows here either way."
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {chatStyle && (
                          <button
                            type="button"
                            onClick={() => setChatStyle(null)}
                            className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                          >
                            Reset all chat view settings
                          </button>
                        )}
                      </div>
                    </CollapsibleSection>
                  </div>
                </div>

                {/* Live preview — sticky alongside the settings on desktop; stacks
                below them on mobile instead of being hidden, so mobile users
                can still see and scroll to it. */}
                <div className="overflow-y-auto border-t lg:border-t-0 lg:border-l bg-gray-50/50 p-4">
                  <div className="lg:sticky lg:top-0 space-y-4">
                    <WidgetPreview
                      genie={genie}
                      selectedTemplate={selectedTemplate}
                      primaryColor={primaryColor}
                      embedStyle={liveEmbedStyle}
                      avatarImageUrl={avatarImageUrl}
                    />

                    <div className="border-t pt-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Actions</h3>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={applyBrandColorsToPopup}>
                          Apply brand colors
                        </Button>
                        {hasCustomEmbedStyling && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleResetToDefaults}
                            disabled={updateAgent.isPending}
                          >
                            Reset to defaults
                          </Button>
                        )}
                        {hasUnsavedChanges && (
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleSavePopupSettings}
                            disabled={updateAgent.isPending}
                            style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
                            className="text-white hover:opacity-90"
                          >
                            {updateAgent.isPending ? "Saving..." : "Save Popup Settings"}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Full Page tab — embed code / live URL only. Styling lives in the
            Full Page Appearance tab below. */}
            <TabsContent value="fullpage" className="flex-1 mt-0 overflow-y-auto">
              <div className="p-8 max-w-2xl">
                <FullPageEmbed
                  htmlCode={fullPageHtmlCode}
                  reactCode={fullPageReactCode}
                  liveUrl={`${baseUrl}/live-agent/${urlName}`}
                />
              </div>
            </TabsContent>

            {/* Website chat and Studio voice settings share the existing full-page namespace. */}
            <TabsContent value="fullpage-appearance" className="flex-1 mt-0 overflow-hidden">
              <div className="grid h-full grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] overflow-hidden">
                <div className="overflow-y-auto p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-700">Customize your website experience</h3>
                    <button
                      onClick={toggleAllFpSections}
                      className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
                    >
                      {fpAllSectionsOpen ? "Collapse all" : "Expand all"}
                    </button>
                  </div>

                  <div className="rounded-xl border border-genie-border bg-genie-primary/5 px-4 py-3">
                    <p className="text-sm text-gray-600">
                      Everything on this tab styles your Genie's own web page, the one visitors open from its link
                      or QR code, and the same page when it's embedded on your site. It applies on desktop and
                      mobile, to the voice screen, the chat and the wrap-up. When embedded, anything left unset
                      here falls back to your Popup Appearance settings. The popup widget itself is styled under
                      Popup Appearance.
                    </p>
                  </div>

                  <CollapsibleSection
                    title="Layout"
                    isOpen={fpOpenSections.has("layout")}
                    onToggle={() => toggleFpSection("layout")}
                  >
                    <p className="text-sm text-gray-500 mb-4">Start from a preset, then adjust any field below.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                      {FULL_PAGE_LAYOUT_PRESETS.map((preset) => {
                        const isSelected =
                          fpLinksResourcesPosition === (preset.layout.linksResourcesPosition ?? "right") &&
                          fpAvatarAlign === (preset.layout.avatarAlign ?? "center") &&
                          fpWelcomePosition === (preset.layout.welcomePosition ?? "above-avatar") &&
                          fpTranscriptPosition === (preset.layout.transcriptPosition ?? "right");
                        return (
                          <button
                            key={preset.id}
                            type="button"
                            onClick={() => {
                              setFpLinksResourcesPosition(preset.layout.linksResourcesPosition ?? "right");
                              setFpAvatarAlign(preset.layout.avatarAlign ?? "center");
                              setFpWelcomePosition(preset.layout.welcomePosition ?? "above-avatar");
                              setFpTranscriptPosition(preset.layout.transcriptPosition ?? "right");
                            }}
                            className={cn(
                              "relative text-left rounded-xl px-4 py-3 transition-all duration-200",
                              isSelected
                                ? "ring-2 ring-genie-primary bg-genie-primary/5"
                                : "ring-1 ring-gray-200 hover:ring-gray-300 bg-white",
                            )}
                          >
                            {isSelected && (
                              <span className="absolute top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-genie-primary">
                                <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                              </span>
                            )}
                            <p
                              className={cn("font-medium text-sm", isSelected ? "text-genie-primary" : "text-gray-900")}
                            >
                              {preset.label}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">{preset.description}</p>
                          </button>
                        );
                      })}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Links &amp; Resources
                        </Label>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {RAIL_POSITION_OPTIONS.filter((opt) => opt.value !== "bottom-center").map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setFpLinksResourcesPosition(opt.value)}
                              className={cn(
                                "px-3 py-1.5 rounded-lg text-sm font-medium transition-all",
                                fpLinksResourcesPosition === opt.value
                                  ? "bg-genie-primary/10 text-genie-primary ring-1 ring-genie-primary"
                                  : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                              )}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Avatar column
                        </Label>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {AVATAR_ALIGN_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setFpAvatarAlign(opt.value)}
                              className={cn(
                                "px-3 py-1.5 rounded-lg text-sm font-medium transition-all",
                                fpAvatarAlign === opt.value
                                  ? "bg-genie-primary/10 text-genie-primary ring-1 ring-genie-primary"
                                  : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                              )}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Welcome text
                        </Label>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {WELCOME_POSITION_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setFpWelcomePosition(opt.value)}
                              className={cn(
                                "px-3 py-1.5 rounded-lg text-sm font-medium transition-all",
                                fpWelcomePosition === opt.value
                                  ? "bg-genie-primary/10 text-genie-primary ring-1 ring-genie-primary"
                                  : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                              )}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Transcript / chat area
                        </Label>
                        <p className="text-xs text-gray-500 mt-1 mb-2">
                          Where the conversation transcript docks once opened.
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {TRANSCRIPT_POSITION_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              title={opt.description}
                              onClick={() => setFpTranscriptPosition(opt.value)}
                              className={cn(
                                "px-3 py-1.5 rounded-lg text-sm font-medium transition-all",
                                fpTranscriptPosition === opt.value
                                  ? "bg-genie-primary/10 text-genie-primary ring-1 ring-genie-primary"
                                  : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                              )}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CollapsibleSection>

                  <CollapsibleSection
                    title="Content"
                    isOpen={fpOpenSections.has("content")}
                    onToggle={() => toggleFpSection("content")}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="fpHideWelcomeMessage">Hide Welcome Message</Label>
                        <p className="text-sm text-gray-500">
                          Removes the greeting line. Position it above/below the avatar instead under Layout.
                        </p>
                      </div>
                      <Switch
                        id="fpHideWelcomeMessage"
                        checked={fpWelcomePosition === "hidden"}
                        onCheckedChange={(checked) => setFpWelcomePosition(checked ? "hidden" : "above-avatar")}
                      />
                    </div>
                  </CollapsibleSection>

                  <CollapsibleSection
                    title="Avatar Photo"
                    isOpen={fpOpenSections.has("avatar")}
                    onToggle={() => toggleFpSection("avatar")}
                  >
                    <p className="text-sm text-gray-500 mb-4">
                      Upload a photo to fill the big central avatar circle on the full-page start screen. This does not
                      change the genie's logo shown near the name — set that from the genie's branding instead.
                    </p>
                    <EnhancedImageUpload
                      value={fpAvatarImageUrl}
                      onChange={(url) => setFpAvatarImageUrl(url)}
                      bucket="genie-images"
                      showColorExtraction={false}
                      showBackgroundRemoval={false}
                      squarePad={false}
                    />

                    {fpAvatarImageUrl && (genie.branding?.logoUrl || genie.branding?.customAvatarUrl) && (
                      <div className="mt-6 pt-6 border-t flex items-center justify-between">
                        <div>
                          <Label htmlFor="fpAvatarShowLogoOverlay">Show Logo on Top</Label>
                          <p className="text-sm text-gray-500">
                            Show the genie's logo as a small badge over the uploaded photo
                          </p>
                        </div>
                        <Switch
                          id="fpAvatarShowLogoOverlay"
                          checked={fpAvatarShowLogoOverlay}
                          onCheckedChange={setFpAvatarShowLogoOverlay}
                        />
                      </div>
                    )}

                    <div className="mt-6 pt-6 border-t">
                      <Label
                        htmlFor="fpAvatarSizeScale"
                        className="text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Avatar Size
                      </Label>
                      <div className="flex items-center gap-3 mt-2">
                        <input
                          id="fpAvatarSizeScale"
                          type="range"
                          min={0.4}
                          max={2.5}
                          step={0.05}
                          value={fpAvatarSizeScale}
                          onChange={(e) => setFpAvatarSizeScale(Number(e.target.value))}
                          className="flex-1"
                        />
                        <span className="text-sm text-gray-600 w-12 text-right">
                          {Math.round(fpAvatarSizeScale * 100)}%
                        </span>
                        {fpAvatarSizeScale !== 1 && (
                          <button
                            type="button"
                            onClick={() => setFpAvatarSizeScale(1)}
                            className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Scales the avatar circle up or down. Applies to both the default genie avatar and the uploaded
                        photo above.
                      </p>
                    </div>
                  </CollapsibleSection>

                  <CollapsibleSection
                    title="Background"
                    isOpen={fpOpenSections.has("background")}
                    onToggle={() => toggleFpSection("background")}
                  >
                    <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Page Background
                    </Label>
                    <div className="flex items-center gap-2 mt-2 mb-3">
                      {(["solid", "gradient", "pattern"] as const).map((type) => {
                        const isSelected = (fullPageBackground?.type ?? "solid") === type;
                        return (
                          <button
                            key={type}
                            type="button"
                            onClick={() =>
                              setFullPageBackground((prev) => ({
                                type,
                                color: prev?.color || "#F9FAFC",
                                color2: prev?.color2,
                                angle: prev?.angle ?? 135,
                                patternId: prev?.patternId ?? "dots",
                              }))
                            }
                            className={cn(
                              "px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-all",
                              isSelected
                                ? "bg-genie-primary/10 text-genie-primary ring-1 ring-genie-primary"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                            )}
                          >
                            {type}
                          </button>
                        );
                      })}
                    </div>

                    {(!fullPageBackground || fullPageBackground.type === "solid") && (
                      <div className="flex items-center gap-2">
                        <ColorSwatchButton
                          value={fullPageBackground?.color || "#F9FAFC"}
                          onChange={(value) => setFullPageBackground({ type: "solid", color: value })}
                          label="Page background color"
                        />
                        <Input
                          type="text"
                          value={fullPageBackground?.color || ""}
                          onChange={(e) => setFullPageBackground({ type: "solid", color: e.target.value })}
                          placeholder="Default (#F9FAFC)"
                          className="flex-1"
                        />
                        <BrandColorQuickPicks
                          colors={backgroundSwatches}
                          onPick={(color) => setFullPageBackground({ type: "solid", color })}
                        />
                      </div>
                    )}

                    {fullPageBackground?.type === "gradient" && (
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs text-gray-500">Start color</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <ColorSwatchButton
                              value={fullPageBackground.color || "#F9FAFC"}
                              onChange={(value) => setFullPageBackground({ ...fullPageBackground, color: value })}
                              label="Gradient start"
                            />
                            <BrandColorQuickPicks
                              colors={backgroundSwatches}
                              onPick={(color) => setFullPageBackground({ ...fullPageBackground, color })}
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">End color</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <ColorSwatchButton
                              value={fullPageBackground.color2 || "#F9FAFC"}
                              onChange={(value) => setFullPageBackground({ ...fullPageBackground, color2: value })}
                              label="Gradient end"
                            />
                            <BrandColorQuickPicks
                              colors={backgroundSwatches}
                              onPick={(color) => setFullPageBackground({ ...fullPageBackground, color2: color })}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {fullPageBackground?.type === "pattern" && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          {(["dots", "grid", "diagonal-lines"] as const).map((patternId) => (
                            <button
                              key={patternId}
                              type="button"
                              onClick={() => setFullPageBackground({ ...fullPageBackground, patternId })}
                              className={cn(
                                "w-14 h-11 rounded-md border-2 transition-all flex-shrink-0",
                                fullPageBackground.patternId === patternId
                                  ? "border-genie-primary ring-2 ring-genie-primary/30"
                                  : "border-gray-300 hover:border-gray-400",
                              )}
                              style={widgetBackgroundToCss({ ...fullPageBackground, patternId }, "#FFFFFF")}
                              title={patternId}
                            />
                          ))}
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Base color</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <ColorSwatchButton
                              value={fullPageBackground.color || "#FFFFFF"}
                              onChange={(value) => setFullPageBackground({ ...fullPageBackground, color: value })}
                              label="Base color"
                            />
                            <BrandColorQuickPicks
                              colors={backgroundSwatches}
                              onPick={(color) => setFullPageBackground({ ...fullPageBackground, color })}
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Pattern Size</Label>
                          <div className="flex items-center gap-3 mt-1">
                            <input
                              type="range"
                              min={4}
                              max={100}
                              step={2}
                              value={
                                fullPageBackground.patternSize ??
                                DEFAULT_PATTERN_SIZE[fullPageBackground.patternId ?? "dots"]
                              }
                              onChange={(e) =>
                                setFullPageBackground({ ...fullPageBackground, patternSize: Number(e.target.value) })
                              }
                              className="flex-1"
                            />
                            <span className="text-sm text-gray-600 w-10 text-right">
                              {fullPageBackground.patternSize ??
                                DEFAULT_PATTERN_SIZE[fullPageBackground.patternId ?? "dots"]}
                              px
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {fullPageBackground && (
                      <button
                        type="button"
                        onClick={() => setFullPageBackground(null)}
                        className="mt-3 text-xs text-gray-500 hover:text-gray-700 hover:underline"
                      >
                        Reset to default
                      </button>
                    )}
                  </CollapsibleSection>

                  <CollapsibleSection
                    title="Buttons"
                    isOpen={fpOpenSections.has("buttons")}
                    onToggle={() => toggleFpSection("buttons")}
                  >
                    <p className="text-sm text-gray-500 mb-4">Talk button</p>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-xs text-gray-500">Color</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <ColorSwatchButton
                            value={fpCtaButton?.color || primaryColor}
                            onChange={(value) => setFpCtaButton({ ...fpCtaButton, color: value })}
                            label="CTA color"
                          />
                          <BrandColorQuickPicks
                            colors={backgroundSwatches}
                            onPick={(color) => setFpCtaButton({ ...fpCtaButton, color })}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Text color</Label>
                        <div className="mt-1">
                          <ColorSwatchButton
                            value={fpCtaButton?.textColor || "#FFFFFF"}
                            onChange={(value) => setFpCtaButton({ ...fpCtaButton, textColor: value })}
                            label="CTA text color"
                          />
                        </div>
                      </div>
                      {fpCtaButton && (
                        <button
                          type="button"
                          onClick={() => setFpCtaButton(null)}
                          className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                        >
                          Reset
                        </button>
                      )}
                    </div>

                    <div className="mt-6 pt-6 border-t">
                      <p className="text-sm text-gray-500 mb-4">Chat button</p>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-xs text-gray-500">Border &amp; text color</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <ColorSwatchButton
                              value={fpChatButton?.color || "#D1D5DB"}
                              onChange={(value) => setFpChatButton({ ...fpChatButton, color: value })}
                              label="Chat button color"
                            />
                            <BrandColorQuickPicks
                              colors={backgroundSwatches}
                              onPick={(color) => setFpChatButton({ ...fpChatButton, color })}
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Background (optional)</Label>
                          <p className="text-xs text-gray-500 mt-0.5 mb-1">
                            Leave unset to keep it outlined/transparent.
                          </p>
                          <div className="flex items-center gap-2">
                            <ColorSwatchButton
                              value={fpChatButton?.backgroundColor || "#FFFFFF"}
                              onChange={(value) => setFpChatButton({ ...fpChatButton, backgroundColor: value })}
                              label="Chat button background"
                            />
                            {fpChatButton?.backgroundColor && (
                              <button
                                type="button"
                                onClick={() => setFpChatButton({ ...fpChatButton, backgroundColor: null })}
                                className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </div>
                        {fpChatButton && (
                          <button
                            type="button"
                            onClick={() => setFpChatButton(null)}
                            className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t">
                      <p className="text-sm text-gray-500 mb-4">Action buttons (mic, chat, mute, etc.)</p>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-xs text-gray-500">Idle background</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <ColorSwatchButton
                              value={fpActionButtons?.idleBackgroundColor || primaryColor}
                              onChange={(value) =>
                                setFpActionButtons({ ...fpActionButtons, idleBackgroundColor: value })
                              }
                              label="Idle background"
                            />
                            <BrandColorQuickPicks
                              colors={backgroundSwatches}
                              onPick={(color) => setFpActionButtons({ ...fpActionButtons, idleBackgroundColor: color })}
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Active background</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <ColorSwatchButton
                              value={fpActionButtons?.activeBackgroundColor || primaryColor}
                              onChange={(value) =>
                                setFpActionButtons({ ...fpActionButtons, activeBackgroundColor: value })
                              }
                              label="Active background"
                            />
                            <BrandColorQuickPicks
                              colors={backgroundSwatches}
                              onPick={(color) =>
                                setFpActionButtons({ ...fpActionButtons, activeBackgroundColor: color })
                              }
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Icon color</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <ColorSwatchButton
                              value={fpActionButtons?.iconColor || primaryColor}
                              onChange={(value) => setFpActionButtons({ ...fpActionButtons, iconColor: value })}
                              label="Icon color"
                            />
                            <BrandColorQuickPicks
                              colors={backgroundSwatches}
                              onPick={(color) => setFpActionButtons({ ...fpActionButtons, iconColor: color })}
                            />
                          </div>
                        </div>
                        {fpActionButtons && (
                          <button
                            type="button"
                            onClick={() => setFpActionButtons(null)}
                            className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </div>
                  </CollapsibleSection>

                  <CollapsibleSection
                    title="Text"
                    isOpen={fpOpenSections.has("text")}
                    onToggle={() => toggleFpSection("text")}
                  >
                    <div>
                      <Label className="text-xs text-gray-500">Welcome message color</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <ColorSwatchButton
                          value={fpWelcomeTextColor || "#666666"}
                          onChange={setFpWelcomeTextColor}
                          label="Welcome message color"
                          unset={!fpWelcomeTextColor}
                        />
                        <BrandColorQuickPicks colors={backgroundSwatches} onPick={setFpWelcomeTextColor} />
                        {!fpWelcomeTextColor && <span className="text-xs text-gray-500">Default</span>}
                        {fpWelcomeTextColor && (
                          <button
                            type="button"
                            onClick={() => setFpWelcomeTextColor(null)}
                            className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </div>
                  </CollapsibleSection>

                  <CollapsibleSection
                    title="Chat View"
                    isOpen={fpOpenSections.has("chat")}
                    onToggle={() => toggleFpSection("chat")}
                  >
                    <p className="text-sm text-gray-500 mb-4">
                      Customize your website chat using the same appearance controls as the widget. These settings are
                      saved for the website. The button below copies your widget's chat settings and action-button
                      colours into this website draft.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      className="mb-4 min-h-11"
                      onClick={() => {
                        setFpChatStyle(chatStyle);
                        setFpActionButtons(actionButtons);
                      }}
                    >
                      Use widget chat settings
                    </Button>
                    <div className="mb-6 lg:hidden">
                      <FullPagePreview
                        genie={genie}
                        primaryColor={primaryColor}
                        fullPageStyle={buildFullPageStylePayload()}
                        defaultTab="chat"
                      />
                    </div>

                    <div>
                      <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Input Background
                      </Label>
                      <div className="flex items-center gap-2 mt-2">
                        <ColorSwatchButton
                          value={fpChatStyle?.inputBackgroundColor || "#FFFFFF"}
                          onChange={(value) => setFpChatStyle({ ...fpChatStyle, inputBackgroundColor: value })}
                          label="Input background"
                        />
                        <BrandColorQuickPicks
                          colors={backgroundSwatches}
                          onPick={(color) => setFpChatStyle({ ...fpChatStyle, inputBackgroundColor: color })}
                        />
                        {fpChatStyle?.inputBackgroundColor && (
                          <button
                            type="button"
                            onClick={() => setFpChatStyle({ ...fpChatStyle, inputBackgroundColor: null })}
                            className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t flex items-center justify-between">
                      <div>
                        <Label htmlFor="fpChatHideBorder">Show Border</Label>
                        <p className="text-sm text-gray-500">Gradient border around the input pill</p>
                      </div>
                      <Switch
                        id="fpChatHideBorder"
                        checked={!fpChatStyle?.hideBorder}
                        onCheckedChange={(checked) => setFpChatStyle({ ...fpChatStyle, hideBorder: !checked })}
                      />
                    </div>

                    {!fpChatStyle?.hideBorder && (
                      <div className="mt-4">
                        <Label className="text-xs text-gray-500">Border Color</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <ColorSwatchButton
                            value={fpChatStyle?.borderColor || primaryColor}
                            onChange={(value) => setFpChatStyle({ ...fpChatStyle, borderColor: value })}
                            label="Border color"
                          />
                          <span className="text-xs text-gray-400">to</span>
                          <ColorSwatchButton
                            value={fpChatStyle?.borderColor2 || primaryColor}
                            onChange={(value) => setFpChatStyle({ ...fpChatStyle, borderColor2: value })}
                            label="Border gradient end"
                          />
                          <BrandColorQuickPicks
                            colors={backgroundSwatches}
                            onPick={(color) => setFpChatStyle({ ...fpChatStyle, borderColor: color })}
                          />
                          {(fpChatStyle?.borderColor || fpChatStyle?.borderColor2) && (
                            <button
                              type="button"
                              onClick={() => setFpChatStyle({ ...fpChatStyle, borderColor: null, borderColor2: null })}
                              className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                            >
                              Reset
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mt-6 pt-6 border-t">
                      <Label className="text-xs text-gray-500">Placeholder Text Color</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <ColorSwatchButton
                          value={fpChatStyle?.placeholderColor || "#9CA3AF"}
                          onChange={(value) => setFpChatStyle({ ...fpChatStyle, placeholderColor: value })}
                          label="Placeholder color"
                        />
                        <BrandColorQuickPicks
                          colors={backgroundSwatches}
                          onPick={(color) => setFpChatStyle({ ...fpChatStyle, placeholderColor: color })}
                        />
                        {fpChatStyle?.placeholderColor && (
                          <button
                            type="button"
                            onClick={() => setFpChatStyle({ ...fpChatStyle, placeholderColor: null })}
                            className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t">
                      <Label className="text-xs text-gray-500">Plus / Attach Button Color</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <ColorSwatchButton
                          value={fpChatStyle?.plusButtonColor || primaryColor}
                          onChange={(value) => setFpChatStyle({ ...fpChatStyle, plusButtonColor: value })}
                          label="Plus button color"
                        />
                        <BrandColorQuickPicks
                          colors={backgroundSwatches}
                          onPick={(color) => setFpChatStyle({ ...fpChatStyle, plusButtonColor: color })}
                        />
                        {fpChatStyle?.plusButtonColor && (
                          <button
                            type="button"
                            onClick={() => setFpChatStyle({ ...fpChatStyle, plusButtonColor: null })}
                            className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t flex items-center justify-between">
                      <div>
                        <Label htmlFor="fpChatSendButtonUseActionStyle">Match Action Buttons Style</Label>
                        <p className="text-sm text-gray-500">
                          Use the Action Buttons colors (set in Buttons) for the send button, instead of the default
                          brand gradient
                        </p>
                      </div>
                      <Switch
                        id="fpChatSendButtonUseActionStyle"
                        checked={!!fpChatStyle?.sendButtonUseActionStyle}
                        onCheckedChange={(checked) =>
                          setFpChatStyle({ ...fpChatStyle, sendButtonUseActionStyle: checked })
                        }
                      />
                    </div>

                    <div className="mt-6 pt-6 border-t space-y-5">
                      <Label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Empty State</Label>
                      <p className="text-xs text-gray-500 -mt-3">
                        The icon, title, and subtitle shown before any messages exist.
                      </p>
                      {(fpChatStyle?.elementOrder ?? EMPTY_STATE_ELEMENTS).map((el, index, order) => {
                        const isHidden = (fpChatStyle?.hiddenElements ?? []).includes(el);
                        const align = fpChatStyle?.elementAlign?.[el] ?? "center";

                        const moveElement = (direction: -1 | 1) => {
                          const currentOrder = fpChatStyle?.elementOrder ?? EMPTY_STATE_ELEMENTS;
                          const newOrder = [...currentOrder];
                          const targetIndex = index + direction;
                          if (targetIndex < 0 || targetIndex >= newOrder.length) return;
                          [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
                          setFpChatStyle({ ...fpChatStyle, elementOrder: newOrder });
                        };

                        const toggleHidden = () => {
                          const hiddenSet = new Set(fpChatStyle?.hiddenElements ?? []);
                          if (hiddenSet.has(el)) hiddenSet.delete(el);
                          else hiddenSet.add(el);
                          setFpChatStyle({ ...fpChatStyle, hiddenElements: Array.from(hiddenSet) });
                        };

                        const setAlign = (value: "left" | "center" | "right") => {
                          setFpChatStyle({
                            ...fpChatStyle,
                            elementAlign: { ...fpChatStyle?.elementAlign, [el]: value },
                          });
                        };

                        return (
                          <div key={el} className="rounded-lg border border-gray-200 p-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">{EMPTY_STATE_LABELS[el]}</span>
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => moveElement(-1)}
                                    disabled={index === 0}
                                    className="min-h-11 min-w-11 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-500"
                                    aria-label={`Move ${el} up`}
                                  >
                                    ↑
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => moveElement(1)}
                                    disabled={index === order.length - 1}
                                    className="min-h-11 min-w-11 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-500"
                                    aria-label={`Move ${el} down`}
                                  >
                                    ↓
                                  </button>
                                </div>
                                <Switch checked={!isHidden} onCheckedChange={toggleHidden} aria-label={`Show ${el}`} />
                              </div>
                            </div>

                            {!isHidden && (
                              <div className="mt-3 space-y-3">
                                <div className="flex items-center gap-2">
                                  {(["left", "center", "right"] as const).map((alignOption) => (
                                    <button
                                      key={alignOption}
                                      type="button"
                                      onClick={() => setAlign(alignOption)}
                                      className={cn(
                                        "min-h-11 min-w-11 px-2.5 rounded-lg text-xs font-medium capitalize transition-all",
                                        align === alignOption
                                          ? "bg-genie-primary/10 text-genie-primary ring-1 ring-genie-primary"
                                          : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                                      )}
                                    >
                                      {alignOption}
                                    </button>
                                  ))}
                                </div>

                                {el === "title" && (
                                  <Input
                                    type="text"
                                    value={fpChatStyle?.titleText || ""}
                                    className="text-base sm:text-sm"
                                    aria-label="Website chat title"
                                    onChange={(e) =>
                                      setFpChatStyle({ ...fpChatStyle, titleText: e.target.value || null })
                                    }
                                    placeholder="Ask {genie name} anything"
                                  />
                                )}
                                {el === "subtitle" && (
                                  <Input
                                    type="text"
                                    value={fpChatStyle?.subtitleText || ""}
                                    className="text-base sm:text-sm"
                                    aria-label="Website chat introduction"
                                    onChange={(e) =>
                                      setFpChatStyle({ ...fpChatStyle, subtitleText: e.target.value || null })
                                    }
                                    placeholder="Type a question to get started."
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {fpChatStyle && (
                        <button
                          type="button"
                          onClick={() => setFpChatStyle(null)}
                          className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                        >
                          Reset all chat view settings
                        </button>
                      )}
                    </div>
                  </CollapsibleSection>
                  <div className="sticky bottom-0 z-10 flex justify-start border-t border-genie-border bg-white py-4">
                    <Button
                      type="button"
                      onClick={handleSaveFullPageSettings}
                      disabled={!hasUnsavedFullPageChanges || updateAgent.isPending}
                      className="min-h-11 bg-gradient-to-r from-genie-primary to-genie-secondary text-white font-semibold hover:opacity-90"
                    >
                      {updateAgent.isPending ? "Saving..." : "Save Full Page Settings"}
                    </Button>
                  </div>
                </div>

                {/* Live preview — sticky alongside the settings so changes are visible as you edit.
                    One FullPagePreview, tabbed (Start screen / Talking / Chat view), instead of a
                    separate WebsiteChatPreview stacked above it. */}
                <div className="hidden overflow-y-auto border-l bg-gray-50/50 p-4 lg:block">
                  <div className="sticky top-0 space-y-4">
                    <FullPagePreview
                      genie={genie}
                      primaryColor={primaryColor}
                      fullPageStyle={buildFullPageStylePayload()}
                    />

                    <div className="border-t pt-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Actions</h3>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={applyBrandColorsToFullPage}>
                          Apply brand colors
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={applyPopupConfigToFullPage}>
                          Apply popup widget config
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={resetFullPageToDefaults}>
                          Reset to defaults
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent value="chat" className="mt-0 min-w-0 flex-1">
          <ChatWidgetEmbed genie={genie} />
        </TabsContent>
        <TabsContent value="email" className="mt-0 min-w-0 flex-1">
          <div className="px-4">
            <h2 className="text-lg font-semibold text-gray-900">Email Widget</h2>
            <p className="mt-1 text-sm text-gray-600">
              Let visitors ask a question on your website and continue the conversation by email.
            </p>
          </div>
          <EmailWidgetEmbed genie={genie} />
        </TabsContent>
        {(["phone", "qr"] as const).map((kind) => (
          <TabsContent key={kind} value={kind} className="mt-0 min-w-0 flex-1">
            <div className="px-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {kind === "phone" ? "Phone Widget" : "QR Code Widget"}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {kind === "phone"
                  ? "Let visitors call your Genie directly from your website."
                  : "Help visitors move from your website to a conversation on their phone."}
              </p>
            </div>
            <DistributionWidgetEmbed genie={genie} kind={kind} />
          </TabsContent>
        ))}
      </Tabs>
    </CoreDrawer>
  );
};

export default WebEmbedModal;
